import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Trash, ArrowsClockwise, Check, X, Crown, Star, Lightning, Hourglass, Lightbulb, TrendUp, TrendDown, Warning, Lock, ChartLine, CaretDown, CaretUp, Plus, ArrowRight, PencilSimpleLine } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CryptoPaymentModal from "../components/CryptoPaymentModal";
import UpgradeModal from "../components/UpgradeModal";
import AlertPopup from "../components/AlertPopup";
import { useAlerts, syncAlertSettingsFromBackend } from "../hooks/useAlerts";
import { useLanguage } from "../context/LanguageContext";
import { useSharedProfile } from "../context/SharedProfileContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Normalize any error shape (string, FastAPI string detail, or Pydantic validation
// error array [{type, loc, msg, input, url}]) into a plain, renderable string.
// React cannot render objects/arrays as children - passing them to toast.error()
// crashes the whole tree (React error #31).
function getErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item?.msg || "Invalid value").join(". ");
  }
  return error?.message || "Failed to save trade";
}

// Coerce a numeric field that may still be a raw string (e.g. locale decimal
// comma, or an un-parsed value) into a proper float before it is sent to the
// backend, which strictly rejects non-numeric strings like "0,98767".
function toSafeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return isNaN(value) ? null : value;
  const parsed = parseFloat(String(value).replace(",", "."));
  return isNaN(parsed) ? null : parsed;
}

const FREE_TRADE_LIMIT = 10;
const INSIGHTS_MIN_TRADES = 10;
const MIN_SAMPLE_SIZE = 5; // Minimum trades per group for reliable comparison
const MIN_DIFF_THRESHOLD = 15; // Minimum percentage point difference for meaningful insight
const MAX_IMBALANCE_RATIO = 4; // Max ratio between group sizes (avoid 20 vs 3 comparisons)

/**
 * Generate trading insights from trade data (V2).
 * Returns an array of insight objects with type and text.
 * Combines momentum-based and structural insights.
 * Returns { insights: [], hasLowQualityData: boolean }
 */
function generateInsights(trades, setups = []) {
  if (!trades || !Array.isArray(trades)) return { insights: [], hasLowQualityData: false };
  
  // Build setup name lookup map
  const setupNameMap = {};
  if (setups && Array.isArray(setups)) {
    setups.forEach(s => {
      if (s.id && s.name) {
        setupNameMap[s.id] = s.name;
      }
    });
  }
  
  // Filter to only closed trades with valid data
  const validTrades = trades.filter(t => 
    t.status === 'closed' && 
    t.outcome && 
    ['win', 'loss', 'breakeven'].includes(t.outcome)
  );
  
  // Get canceled/not-triggered trades for planned trades behavior insight
  const canceledTrades = trades.filter(t => t.status === 'canceled');
  
  if (validTrades.length < INSIGHTS_MIN_TRADES) {
    return { insights: [], hasLowQualityData: false };
  }
  
  const insights = [];
  
  // Helper: Calculate win rate for a group of trades
  const calcWinRate = (group) => {
    if (!group || group.length === 0) return 0;
    const wins = group.filter(t => t.outcome === 'win').length;
    return (wins / group.length) * 100;
  };
  
  // Helper: Calculate average RR for a group
  const calcAvgRR = (group) => {
    if (!group || group.length === 0) return 0;
    const withRR = group.filter(t => t.rr != null && !isNaN(t.rr));
    if (withRR.length === 0) return 0;
    return withRR.reduce((sum, t) => sum + t.rr, 0) / withRR.length;
  };
  
  // Helper: Check if comparison is statistically balanced
  const isBalancedComparison = (groupA, groupB) => {
    if (!groupA || !groupB) return false;
    if (groupA.length < MIN_SAMPLE_SIZE || groupB.length < MIN_SAMPLE_SIZE) return false;
    const ratio = Math.max(groupA.length, groupB.length) / Math.min(groupA.length, groupB.length);
    return ratio <= MAX_IMBALANCE_RATIO;
  };
  
  // Helper: Check if win rate difference is meaningful
  const isMeaningfulDifference = (rateA, rateB, groupASize, groupBSize, threshold = MIN_DIFF_THRESHOLD) => {
    const diff = Math.abs(rateA - rateB);
    if (diff < threshold) return false;
    const minSize = Math.min(groupASize, groupBSize);
    if (minSize < 8 && (rateA === 100 || rateA === 0 || rateB === 100 || rateB === 0)) {
      return false;
    }
    return true;
  };
  
  // Sort trades by close date (most recent first)
  const sortedTrades = [...validTrades].sort((a, b) => 
    new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at)
  );
  
  // ============================================
  // INSIGHT 1: RR MOMENTUM (HIGH PRIORITY)
  // Compare last 10 trades vs previous 10 trades
  // ============================================
  if (sortedTrades.length >= 20) {
    const recentTrades = sortedTrades.slice(0, 10);
    const previousTrades = sortedTrades.slice(10, 20);
    
    const avgRR_recent = calcAvgRR(recentTrades);
    const avgRR_previous = calcAvgRR(previousTrades);
    
    // Only calculate if previous avg is meaningful (not near zero)
    if (Math.abs(avgRR_previous) > 0.1) {
      const deltaPercent = ((avgRR_recent - avgRR_previous) / Math.abs(avgRR_previous)) * 100;
      
      // Trigger only if change is >= 25%
      if (Math.abs(deltaPercent) >= 25) {
        if (deltaPercent > 0) {
          insights.push({
            type: 'positive',
            text: `Your recent RR is improving`,
            subtext: `Your last 10 trades show stronger RR consistency`
          });
        } else {
          insights.push({
            type: 'warning',
            text: `Your recent RR is slipping`,
            subtext: `Review the quality of your recent setups to stay consistent`,
            // Navigation to History with recent RR trades view
            link: {
              path: '/history',
              params: { view: 'recent-rr' }
            }
          });
        }
      }
    }
  }
  
  // ============================================
  // INSIGHT 2: PLANNED TRADES BEHAVIOR (NEW)
  // Analyze not-triggered trades and skipped executions
  // ============================================
  const MIN_PLANNED_TRADES = 10;
  
  if (canceledTrades.length >= MIN_PLANNED_TRADES) {
    // Calculate total planned trades (executed + canceled)
    const totalPlannedTrades = validTrades.length + canceledTrades.length;
    
    // Calculate not-triggered rate
    const notTriggeredRate = (canceledTrades.length / totalPlannedTrades) * 100;
    
    // Count skipped executions specifically
    const skippedExecutions = canceledTrades.filter(t => 
      t.cancel_reason === 'skipped_execution' || 
      (!t.cancel_reason) // treat unknown as skipped for safety
    );
    const skippedExecutionRate = (skippedExecutions.length / totalPlannedTrades) * 100;
    
    // Track if we added a planned trades insight (avoid redundant warnings)
    let addedPlannedInsight = false;
    
    // Check for high skipped execution rate first (more behavioral/actionable)
    if (skippedExecutionRate >= 30 && !addedPlannedInsight) {
      insights.push({
        type: 'warning',
        text: `You're skipping executions frequently`,
        subtext: `Executing your plan more consistently may improve discipline`
      });
      addedPlannedInsight = true;
    }
    
    // Check for high not-triggered rate (only if not already showing skipped warning)
    if (notTriggeredRate >= 50 && !addedPlannedInsight) {
      insights.push({
        type: 'warning',
        text: `Many of your planned setups never reach execution`,
        subtext: `Review your entry levels or market selection`
      });
      addedPlannedInsight = true;
    }
    
    // Positive insight: Strong execution discipline
    // Only show if behavior is genuinely healthy and sample size is sufficient
    if (!addedPlannedInsight && 
        notTriggeredRate < 25 && 
        skippedExecutionRate < 15 && 
        totalPlannedTrades >= 20) {
      insights.push({
        type: 'positive',
        text: `You're executing your planned setups consistently`,
        subtext: `Most of your planned trades are being followed through`
      });
    }
  }
  
  // ============================================
  // INSIGHT 3: CONFIDENCE (KEEP CURRENT)
  // Compare low vs higher confidence trades
  // Skip if confidence tracking is disabled (null values)
  // ============================================
  const lowConfTrades = validTrades.filter(t => t.confidence === 'low');
  const highConfTrades = validTrades.filter(t => t.confidence === 'high' || t.confidence === 'medium');
  const tradesWithConfidence = validTrades.filter(t => t.confidence != null);
  
  // Only run confidence insights if enough trades have confidence tracked
  if (tradesWithConfidence.length >= 5 && isBalancedComparison(lowConfTrades, highConfTrades)) {
    const lowConfWinRate = calcWinRate(lowConfTrades);
    const highConfWinRate = calcWinRate(highConfTrades);
    
    if (isMeaningfulDifference(lowConfWinRate, highConfWinRate, lowConfTrades.length, highConfTrades.length)) {
      if (lowConfWinRate < highConfWinRate) {
        insights.push({
          type: 'warning',
          text: `Low confidence trades are hurting your performance`
        });
      } else {
        insights.push({
          type: 'positive',
          text: `Your low confidence reads are actually solid — trust your analysis`
        });
      }
    }
  }
  
  // ============================================
  // INSIGHT 4: SETUP PERFORMANCE - COMPREHENSIVE
  // Multiple setup-level insights with profit factor analysis
  // ============================================
  if (validTrades.length >= 10) {
    // Group by setup_id to preserve the ID for navigation
    // Use setup_id as key, store both name and id
    const setupGroups = {};
    validTrades.forEach(t => {
      const setupId = t.setup_id || '__no_setup__';
      // Use setupNameMap to get the correct name (more reliable than t.setup_name)
      const setupName = setupId !== '__no_setup__' 
        ? (setupNameMap[setupId] || t.setup_name || 'Unknown Setup')
        : 'No setup';
      if (!setupGroups[setupId]) {
        setupGroups[setupId] = {
          id: setupId,
          name: setupName,
          trades: []
        };
      }
      setupGroups[setupId].trades.push(t);
    });
    
    // Calculate Profit Factor for a set of trades
    const calcProfitFactor = (trades) => {
      const wins = trades.filter(t => t.outcome === 'win');
      const losses = trades.filter(t => t.outcome === 'loss');
      const totalWinRR = wins.reduce((sum, t) => sum + (t.rr || 0), 0);
      const totalLossRR = losses.reduce((sum, t) => sum + Math.abs(t.rr || 0), 0);
      if (totalLossRR === 0) return totalWinRR > 0 ? Infinity : 0;
      return totalWinRR / totalLossRR;
    };
    
    // Build setup analytics with all metrics (including setup_id for navigation)
    const setupAnalytics = Object.values(setupGroups)
      .filter(group => group.id !== '__no_setup__')
      .map(group => ({
        id: group.id,  // Capture setup_id for navigation
        name: group.name,
        trades: group.trades.length,
        winRate: calcWinRate(group.trades),
        avgRR: calcAvgRR(group.trades),
        profitFactor: calcProfitFactor(group.trades)
      }));
    
    // Track which insights we've added to avoid duplicates
    const addedSetupInsights = new Set();
    
    // 4.1 MOST USED BUT UNDERPERFORMING
    // Condition: highest trades AND profit factor < 1.2
    if (setupAnalytics.length > 0) {
      const mostUsed = [...setupAnalytics].sort((a, b) => b.trades - a.trades)[0];
      if (mostUsed.trades >= MIN_SAMPLE_SIZE && mostUsed.profitFactor < 1.2 && mostUsed.profitFactor !== Infinity) {
        insights.push({
          type: 'warning',
          text: `You're frequently trading ${mostUsed.name}, but performance is below optimal`,
          subtext: `Consider reviewing your entries or reducing exposure to this setup`,
          // Navigation to setup analytics page
          link: {
            path: `/analytics/setup/${mostUsed.id}`
          }
        });
        addedSetupInsights.add('most_used_underperform');
      }
    }
    
    // 4.2 TOP PERFORMING SETUP
    // Condition: highest profit factor AND trades >= 5
    const qualifiedForTop = setupAnalytics.filter(s => s.trades >= MIN_SAMPLE_SIZE && s.profitFactor !== Infinity);
    if (qualifiedForTop.length > 0) {
      const topPerforming = [...qualifiedForTop].sort((a, b) => b.profitFactor - a.profitFactor)[0];
      if (topPerforming.profitFactor >= 1.5) {
        insights.push({
          type: 'positive',
          text: `${topPerforming.name} is currently your best performing setup`,
          subtext: `Consider focusing more on this pattern`
        });
        addedSetupInsights.add('top_performing');
      }
    }
    
    // 4.3 NEEDS REVIEW SETUP
    // Condition: lowest profit factor AND trades >= 5 AND profit factor < 1.0
    if (qualifiedForTop.length > 0) {
      const needsReview = [...qualifiedForTop].sort((a, b) => a.profitFactor - b.profitFactor)[0];
      if (needsReview.profitFactor < 1.0) {
        insights.push({
          type: 'warning',
          text: `${needsReview.name} is underperforming`,
          subtext: `This setup may need adjustments or should be avoided`,
          // Navigation to setup analytics page
          link: {
            path: `/analytics/setup/${needsReview.id}`
          }
        });
        addedSetupInsights.add('needs_review');
      }
    }
    
    // 4.4 HIGH CONSISTENCY SETUP
    // Condition: trades >= 5 AND profit factor >= 1.5
    const reliableSetups = setupAnalytics.filter(s => 
      s.trades >= MIN_SAMPLE_SIZE && 
      s.profitFactor >= 1.5 && 
      s.profitFactor !== Infinity
    );
    if (reliableSetups.length > 0 && !addedSetupInsights.has('top_performing')) {
      const mostReliable = [...reliableSetups].sort((a, b) => b.profitFactor - a.profitFactor)[0];
      insights.push({
        type: 'positive',
        text: `${mostReliable.name} is one of your most reliable setups`,
        subtext: `Its performance is consistently strong across your recent trades`
      });
      addedSetupInsights.add('high_consistency');
    }
  }
  
  // ============================================
  // INSIGHT 5: NO SETUP (KEEP CURRENT)
  // Compare trades with vs without setup
  // ============================================
  const noSetupTrades = validTrades.filter(t => !t.setup_name || t.setup_name === 'No setup');
  const withSetupTrades = validTrades.filter(t => t.setup_name && t.setup_name !== 'No setup');
  
  if (isBalancedComparison(noSetupTrades, withSetupTrades)) {
    const noSetupWinRate = calcWinRate(noSetupTrades);
    const withSetupWinRate = calcWinRate(withSetupTrades);
    
    if (isMeaningfulDifference(noSetupWinRate, withSetupWinRate, noSetupTrades.length, withSetupTrades.length)) {
      if (noSetupWinRate < withSetupWinRate) {
        insights.push({
          type: 'warning',
          text: `Trades without a setup are reducing your consistency`
        });
      }
    }
  }
  
  // ============================================
  // INSIGHT 6: TIMEFRAME PERFORMANCE
  // Find best performing timeframe
  // ============================================
  const timeframeGroups = {};
  validTrades.forEach(t => {
    const tf = t.timeframe || 'Unknown';
    if (tf !== 'Unknown') {
      if (!timeframeGroups[tf]) timeframeGroups[tf] = [];
      timeframeGroups[tf].push(t);
    }
  });
  
  // Filter timeframes with enough trades (minimum 5)
  const qualifiedTimeframes = Object.entries(timeframeGroups)
    .filter(([tf, trades]) => trades.length >= MIN_SAMPLE_SIZE)
    .map(([tf, trades]) => ({
      timeframe: tf,
      trades,
      winRate: calcWinRate(trades)
    }))
    .sort((a, b) => b.winRate - a.winRate);
  
  // Need at least 2 timeframes to compare
  if (qualifiedTimeframes.length >= 2) {
    const bestTimeframe = qualifiedTimeframes[0];
    const overallWinRate = calcWinRate(validTrades);
    
    // Trigger if best timeframe is at least 10% better than overall average
    if (bestTimeframe.winRate - overallWinRate >= 10) {
      insights.push({
        type: 'positive',
        text: `${bestTimeframe.timeframe} is currently your most consistent timeframe`,
        subtext: `You perform better on this timeframe than others`
      });
    }
  }
  
  // ============================================
  // PARTIAL EXECUTION INSIGHT (Negative Impact Only)
  // ============================================
  // Check BOTH global AND per-setup for partial problems
  // Analytics shows problems per-setup, so Dashboard must also check per-setup
  const completedTrades = validTrades.filter(t => 
    t.outcome === 'win' || t.outcome === 'loss'
  );
  
  // Group trades by setup for per-setup analysis
  const setupTradeGroups = {};
  completedTrades.forEach(t => {
    const setupKey = t.setup_id || '__no_setup__';
    // Look up setup name from map, fallback to trade fields, then 'No setup'
    const setupName = setupNameMap[t.setup_id] || t.setup_name || t.setup || 'No setup';
    if (!setupTradeGroups[setupKey]) {
      setupTradeGroups[setupKey] = { name: setupName, trades: [] };
    }
    setupTradeGroups[setupKey].trades.push(t);
  });
  
  // Find worst setup based on partial execution
  let worstPartialSetup = null;
  let worstPartialSetupId = null;
  let worstPartialMetrics = null;
  
  Object.entries(setupTradeGroups).forEach(([setupKey, group]) => {
    const setupTrades = group.trades.filter(t => t.hand_size > 0);
    const withPartial = setupTrades.filter(t => t.partial_used === true);
    const withoutPartial = setupTrades.filter(t => !t.partial_used);
    
    // Need minimum sample sizes (same as Analytics: 3 each)
    if (withPartial.length >= 3 && withoutPartial.length >= 3) {
      const avgPartialR = withPartial.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / withPartial.length;
      const avgNoPartialR = withoutPartial.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / withoutPartial.length;
      const deltaR = avgPartialR - avgNoPartialR;
      
      // Check for "given back" metric (needs first_partial_rr)
      const partialWithRR = withPartial.filter(t => 
        t.first_partial_rr !== null && t.first_partial_rr !== undefined
      );
      
      let avgGivenBack = 0;
      if (partialWithRR.length >= 3) {
        avgGivenBack = partialWithRR.reduce((sum, t) => {
          const finalR = t.profit_usd / t.hand_size;
          return sum + Math.max(0, t.first_partial_rr - finalR);
        }, 0) / partialWithRR.length;
      }
      
      // Check for problems (same thresholds as Analytics)
      // deltaR <= -0.25 means partial is worse
      // avgGivenBack >= 0.5 means giving back too much
      const hasDeltaProblem = deltaR <= -0.25;
      const hasGiveBackProblem = avgGivenBack >= 0.5;
      
      if (hasDeltaProblem || hasGiveBackProblem) {
        // Track if this is the worst setup
        const severity = Math.abs(deltaR) + avgGivenBack;
        if (!worstPartialMetrics || severity > worstPartialMetrics.severity) {
          worstPartialSetup = group.name;
          worstPartialSetupId = setupKey; // Store the setup_id for navigation
          worstPartialMetrics = {
            deltaR,
            avgGivenBack,
            avgPartialR,
            avgNoPartialR,
            severity,
            hasDeltaProblem,
            hasGiveBackProblem
          };
        }
      }
    }
  });
  
  // Debug log (remove after verification)
  if (worstPartialMetrics) {
    console.log("Partial Dashboard Insight:", {
      setup: worstPartialSetup,
      metrics: worstPartialMetrics
    });
  }
  
  // If we found a setup with partial problems, show insight
  if (worstPartialSetup && worstPartialMetrics) {
    let insightText, insightSubtext;
    
    if (worstPartialSetup !== 'No setup') {
      insightText = `Partial execution is hurting your edge on ${worstPartialSetup}`;
      if (worstPartialMetrics.hasGiveBackProblem) {
        insightSubtext = `You're giving back ${worstPartialMetrics.avgGivenBack.toFixed(2)}R on average after first partial. Consider reducing partial usage or improving post-partial management.`;
      } else {
        insightSubtext = `Trades without partial outperform trades with partial by ${Math.abs(worstPartialMetrics.deltaR).toFixed(2)}R on this setup.`;
      }
    } else {
      insightText = `Partial execution is hurting your performance`;
      insightSubtext = `Trades without partial are outperforming trades with partial.`;
    }
    
    insights.push({
      type: 'warning',
      text: insightText,
      subtext: insightSubtext,
      _priority: 1,  // Highest priority - Partial Execution
      // Navigation metadata for clickable insight
      link: {
        path: '/advanced/partial-execution',
        params: worstPartialSetupId ? { setup: worstPartialSetupId } : null
      }
    });
  }
  
  // ============================================
  // PLANNED TRADES (NOT TRIGGERED) PROBLEM INSIGHT
  // ============================================
  // Only show if significant portion of trades are being canceled/not executed
  // Note: canceledTrades already declared at top of function
  const totalNotTriggered = canceledTrades.length;
  const totalTradesCount = trades.length;
  
  // Minimum data requirement: (total >= 15 OR not_triggered >= 10) AND rate >= 30%
  const notTriggeredRate = totalTradesCount > 0 ? totalNotTriggered / totalTradesCount : 0;
  const hasEnoughPlannedData = totalTradesCount >= 15 || totalNotTriggered >= 10;
  
  if (hasEnoughPlannedData && notTriggeredRate >= 0.30 && totalNotTriggered > 0) {
    // Calculate breakdown
    const plannedCounts = {
      setup_not_triggered: 0,
      no_entry_reach: 0,
      skipped_execution: 0
    };
    
    canceledTrades.forEach(t => {
      const reason = t.cancel_reason;
      if (reason && plannedCounts.hasOwnProperty(reason)) {
        plannedCounts[reason]++;
      } else {
        plannedCounts.skipped_execution++;
      }
    });
    
    // Calculate rates
    const entryRate = totalNotTriggered > 0 ? plannedCounts.no_entry_reach / totalNotTriggered : 0;
    const setupRate = totalNotTriggered > 0 ? plannedCounts.setup_not_triggered / totalNotTriggered : 0;
    const skippedRate = totalNotTriggered > 0 ? plannedCounts.skipped_execution / totalNotTriggered : 0;
    
    // Determine dominant cause
    const rates = [
      { cause: 'entry', rate: entryRate },
      { cause: 'setup', rate: setupRate },
      { cause: 'execution', rate: skippedRate }
    ].sort((a, b) => b.rate - a.rate);
    
    const dominantCause = rates[0].rate > 0 ? rates[0].cause : null;
    
    // Check if a specific setup is worse (group by setup)
    let worstSetup = null;
    const setupCancelGroups = {};
    
    canceledTrades.forEach(t => {
      const setupKey = t.setup_id || '__no_setup__';
      const setupName = t.setup_name || t.setup || 'No setup';
      if (!setupCancelGroups[setupKey]) {
        setupCancelGroups[setupKey] = { name: setupName, count: 0 };
      }
      setupCancelGroups[setupKey].count++;
    });
    
    // Find worst setup (>=3 canceled trades and highest count)
    let worstCount = 0;
    Object.entries(setupCancelGroups).forEach(([key, group]) => {
      if (group.count >= 3 && group.count > worstCount && group.name !== 'No setup') {
        worstCount = group.count;
        worstSetup = group.name;
      }
    });
    
    // Build insight message based on dominant cause
    let plannedText, plannedSubtext;
    
    if (dominantCause === 'entry') {
      plannedText = worstSetup 
        ? `${worstSetup} setups are often not reaching entry`
        : "Many of your setups are not reaching entry";
      plannedSubtext = "Your entry levels may be too far from price action";
    } else if (dominantCause === 'setup') {
      plannedText = worstSetup
        ? `${worstSetup} setups often fail before triggering`
        : "Your setups often fail before triggering";
      plannedSubtext = "You may need to refine your setup conditions";
    } else {
      plannedText = worstSetup
        ? `You're skipping many ${worstSetup} planned trades`
        : "You're skipping many planned trades";
      plannedSubtext = "This may indicate hesitation or lack of discipline";
    }
    
    insights.push({
      type: 'warning',
      text: plannedText,
      subtext: plannedSubtext,
      _priority: 2,  // Second priority - Planned Trades
      // Navigation to Planned Trades detail page
      link: {
        path: '/advanced/planned-trades',
        params: null  // No specific filter needed
      }
    });
  }
  
  // ============================================
  // WINNING RR DEGRADATION PROBLEM INSIGHT
  // ============================================
  // Focus ONLY on winning trades RR - more actionable signal than overall avg RR
  // Removed: avg_rr checks (mixes wins/losses, not actionable)
  if (validTrades.length >= 15) {
    const tradesWithRR = validTrades.filter(t => 
      t.hand_size > 0 && 
      t.profit_usd !== null && 
      t.profit_usd !== undefined
    );
    
    // Focus on winning trades only
    const winningTrades = tradesWithRR.filter(t => t.outcome === 'win');
    
    if (winningTrades.length >= 5) {
      const avgWinRR = winningTrades.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / winningTrades.length;
      
      // Calculate recent vs previous winning RR (last 5 wins vs previous 5 wins)
      const sortedWinsByDate = [...winningTrades].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      let recentWinRR = null;
      let previousWinRR = null;
      let isWinRRTrending = false;
      
      if (sortedWinsByDate.length >= 10) {
        const recent5Wins = sortedWinsByDate.slice(0, 5);
        const previous5Wins = sortedWinsByDate.slice(5, 10);
        
        recentWinRR = recent5Wins.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / 5;
        previousWinRR = previous5Wins.reduce((sum, t) => sum + (t.profit_usd / t.hand_size), 0) / 5;
        
        // Check for meaningful drop (at least 0.3R difference AND previous was decent)
        isWinRRTrending = previousWinRR >= 1.0 && recentWinRR < previousWinRR && (previousWinRR - recentWinRR) >= 0.3;
      }
      
      // Check for consistently low winning RR (threshold: 1.2R is minimum acceptable)
      const isWinRRLow = avgWinRR < 1.2;
      
      // Only show if clear negative signal
      if (isWinRRTrending || isWinRRLow) {
        let rrText, rrSubtext;
        
        // Prioritize trend-based (more actionable) over static low
        if (isWinRRTrending) {
          rrText = "Your recent winning trades have lower RR";
          rrSubtext = `Recent wins: ${recentWinRR.toFixed(2)}R vs Previous: ${previousWinRR.toFixed(2)}R — you may be exiting too early or reducing targets`;
        } else if (isWinRRLow) {
          rrText = "Your winning trades are returning less RR";
          rrSubtext = `Avg win RR: ${avgWinRR.toFixed(2)}R — consider reviewing your exit strategy`;
        }
        
        insights.push({
          type: 'warning',
          text: rrText,
          subtext: rrSubtext,
          _priority: 3,  // Third priority - Winning RR
          // Navigation to History with winning-rr view
          link: {
            path: '/history',
            params: { 
              view: 'winning-rr',
              // Pass condition type so History knows which summary to show
              condition: isWinRRTrending ? 'trend' : 'static'
            }
          }
        });
      }
    }
  }
  
  // ============================================
  // FALLBACK: If user has >=10 trades but no insights
  // ============================================
  if (validTrades.length >= INSIGHTS_MIN_TRADES && insights.length === 0) {
    insights.push({
      type: 'neutral',
      text: `You're starting to build patterns`,
      subtext: `Your data is becoming more consistent — keep logging trades`,
      bullets: [
        `You have ${validTrades.length} trades logged`,
        `More patterns will emerge as your dataset grows`
      ]
    });
  }
  
  // ============================================
  // TRADING STREAK INSIGHT
  // ============================================
  // Detect current trading streak based on ACTIVE TRADING DAYS
  const calculateTradingStreak = () => {
    // Get closed trades with profit data
    const closedTrades = validTrades.filter(t => 
      t.profit_usd !== null && t.profit_usd !== undefined
    );
    
    if (closedTrades.length < 3) return null;
    
    // Group trades by day (YYYY-MM-DD)
    const tradesByDay = {};
    closedTrades.forEach(trade => {
      if (!trade.created_at) return;
      const dayKey = new Date(trade.created_at).toISOString().split('T')[0];
      if (!tradesByDay[dayKey]) {
        tradesByDay[dayKey] = [];
      }
      tradesByDay[dayKey].push(trade);
    });
    
    // Calculate daily PnL and filter active days (non-zero PnL)
    const activeDays = Object.entries(tradesByDay)
      .map(([date, dayTrades]) => {
        const totalPnL = dayTrades.reduce((sum, t) => sum + (t.profit_usd || 0), 0);
        return { date, totalPnL };
      })
      .filter(day => day.totalPnL !== 0) // Filter out zero-PnL days
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort descending (most recent first)
    
    if (activeDays.length < 3) return null;
    
    // Convert to win/loss sequence and calculate current streak
    const sequence = activeDays.map(day => day.totalPnL > 0 ? 'win' : 'loss');
    
    // Calculate streak from most recent day backwards
    const streakType = sequence[0];
    let streakLength = 1;
    
    for (let i = 1; i < sequence.length; i++) {
      if (sequence[i] === streakType) {
        streakLength++;
      } else {
        break; // Streak ended
      }
    }
    
    return { type: streakType, length: streakLength };
  };
  
  const streak = calculateTradingStreak();
  
  // Only show streak insight if meaningful streak exists
  // Winning streak: >= 3 days
  // Losing streak: >= 3 days
  if (streak) {
    if (streak.type === 'loss' && streak.length >= 3) {
      // Losing streak - medium-high priority (warning)
      // Include streak dates for navigation
      insights.push({
        type: 'warning',
        text: `You're on a ${streak.length}-day losing streak`,
        subtext: 'Consider slowing down and reviewing your recent trades',
        link: {
          path: '/history',
          params: { view: 'losing-streak', days: streak.length }
        },
        _priority: 4  // After RR (3), before positive insights
      });
    } else if (streak.type === 'win' && streak.length >= 3) {
      // Winning streak - low priority (positive)
      insights.push({
        type: 'positive',
        text: `You're on a ${streak.length}-day winning streak`,
        subtext: 'Keep following what is working in your recent trades',
        _priority: 10  // Low priority - positive insight
      });
    }
  }
  
  // ============================================
  // PRIORITIZE AND DEDUPLICATE INSIGHTS
  // ============================================
  // Priority order: 1=Partial Execution, 2=Planned Trades, 3=Winning RR, undefined=other
  // Warning insights (with _priority) are sorted first, then others
  const sortedInsights = [...insights].sort((a, b) => {
    // Warning insights with priority come first
    const aPriority = a._priority || 99;
    const bPriority = b._priority || 99;
    return aPriority - bPriority;
  });
  
  // Remove internal priority field before returning
  const cleanedInsights = sortedInsights.map(({ _priority, ...rest }) => rest);
  
  // Return top 3 insights max for dashboard, but keep all for "View all" feature
  const finalInsights = cleanedInsights.slice(0, 3);
  const hasLowQualityData = validTrades.length >= INSIGHTS_MIN_TRADES && finalInsights.length === 0;
  
  return { 
    insights: finalInsights,           // Top 3 for dashboard
    allInsights: cleanedInsights,      // Full sorted list for "View all"
    hasLowQualityData 
  };
}

/**
 * Get progress state based on trade count.
 * Returns stage, title, and messages for the Progress section.
 */
function getProgressState(trades) {
  if (!trades || !Array.isArray(trades)) {
    return { stage: 'early', tradeCount: 0, title: '', messages: [] };
  }
  
  // Count closed trades for consistency
  const closedTrades = trades.filter(t => t.status === 'closed');
  const tradeCount = closedTrades.length;
  
  if (tradeCount < 10) {
    return {
      stage: 'early',
      tradeCount,
      title: "You're building your trading dataset",
      messages: [
        `${tradeCount} / 10 trades logged`,
        "Insights unlock at 10 trades"
      ]
    };
  }
  
  if (tradeCount < 20) {
    return {
      stage: 'growing',
      tradeCount,
      title: "You're starting to see patterns",
      messages: [
        `${tradeCount} trades logged`,
        "Keep going to unlock performance trends"
      ]
    };
  }
  
  return {
    stage: 'consistent',
    tradeCount,
    title: "You're building consistency",
    messages: [
      `${tradeCount} trades logged`,
      "Your data is now reliable for performance analysis"
    ]
  };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  
  // Shared profile mode - when viewing another user's shared profile
  const { isSharedProfileView, isReadOnly } = useSharedProfile();
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isManualMode, setIsManualMode] = useState(false); // Manual trade entry without image
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPasteHint, setShowPasteHint] = useState(false);
  const [setups, setSetups] = useState([]);
  const [setupsError, setSetupsError] = useState(false);
  const [assetGroups, setAssetGroups] = useState([]);
  const [selectedSetup, setSelectedSetup] = useState("");
  const [selectedAssetGroup, setSelectedAssetGroup] = useState("");
  const [assetGroupDetected, setAssetGroupDetected] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState("pending");
  const [positionStatus, setPositionStatus] = useState("active"); // 'active' or 'pending' for open positions
  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [timeframeDetected, setTimeframeDetected] = useState(false);
  const [timeframeConfidence, setTimeframeConfidence] = useState("");
  const [handSize, setHandSize] = useState("");
  const [handSizeEdited, setHandSizeEdited] = useState(false);
  const [lastHandSize, setLastHandSize] = useState(null);
  const [tradeCount, setTradeCount] = useState(0);
  const [allTrades, setAllTrades] = useState([]); // Store all trades for insights
  const [tradesLoading, setTradesLoading] = useState(true);
  const [tradeLimitStatus, setTradeLimitStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showProSuccessModal, setShowProSuccessModal] = useState(false);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [cryptoModalPlan, setCryptoModalPlan] = useState(null);
  const [pendingCryptoPayment, setPendingCryptoPayment] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('free'); // 'free' or 'pro'
  const [subscriptionLoading, setSubscriptionLoading] = useState(true); // Track subscription loading state
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [userEmail, setUserEmail] = useState(null); // Store user email for admin checks
  const [insightsExpanded, setInsightsExpanded] = useState(false); // Collapsible insights section
  const [hasUnseenInsights, setHasUnseenInsights] = useState(false); // Track unseen insights
  const [insightsShowAll, setInsightsShowAll] = useState(false); // Show all insights inline
  
  // Confidence tracking preference
  // Read from localStorage first for instant initialization, then sync with backend
  const [confidenceTrackingEnabled, setConfidenceTrackingEnabled] = useState(() => {
    try {
      const cached = localStorage.getItem('geni_confidence_tracking_enabled');
      if (cached !== null) {
        return cached === 'true';
      }
    } catch (e) {
      console.error('Error reading confidence tracking preference from localStorage:', e);
    }
    return true; // Default to true if no cached value
  });
  const [showConfidenceDisableModal, setShowConfidenceDisableModal] = useState(false);
  const [pendingConfidenceDisable, setPendingConfidenceDisable] = useState(false);
  
  // Track if user has confirmed "do not track confidence" warning (one-time modal)
  const [hasConfirmedDoNotTrackConfidence, setHasConfirmedDoNotTrackConfidence] = useState(() => {
    try {
      const cached = localStorage.getItem('hasConfirmedDoNotTrackConfidence');
      return cached === 'true';
    } catch (e) {
      console.error('Error reading hasConfirmedDoNotTrackConfidence from localStorage:', e);
    }
    return false;
  });
  
  // Create Setup Modal state (reuses Analytics pattern)
  const [showCreateSetupModal, setShowCreateSetupModal] = useState(false);
  const [newSetupName, setNewSetupName] = useState("");
  const [newSetupDescription, setNewSetupDescription] = useState("");
  const [creatingSetup, setCreatingSetup] = useState(false);
  
  // Trade validation error state (Entry/Stop Loss/Take Profit consistency)
  const [tradeValidationError, setTradeValidationError] = useState(null);
  
  // Confidence validation error state
  const [confidenceError, setConfidenceError] = useState(null);
  
  // Expectancy warning confirmation modal state
  const [showExpectancyWarning, setShowExpectancyWarning] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  
  // Alert system
  const { alerts, checkAlerts, dismissAlert } = useAlerts();
  const [userCapital, setUserCapital] = useState(0);
  const [currentCapital, setCurrentCapital] = useState(0);
  
  // Calculate setup statistics from allTrades
  const setupStats = useMemo(() => {
    const stats = {};
    
    // Only include completed trades (WIN or LOSS)
    const completedTrades = allTrades.filter(t => 
      t.status === 'closed' && 
      (t.outcome === 'win' || t.outcome === 'loss') &&
      t.setup_id
    );
    
    // Group by setup_id
    completedTrades.forEach(trade => {
      if (!stats[trade.setup_id]) {
        stats[trade.setup_id] = { wins: 0, losses: 0, total: 0 };
      }
      stats[trade.setup_id].total++;
      if (trade.outcome === 'win') {
        stats[trade.setup_id].wins++;
      } else {
        stats[trade.setup_id].losses++;
      }
    });
    
    // Calculate win rate for each setup
    Object.keys(stats).forEach(setupId => {
      const s = stats[setupId];
      s.winRate = s.total > 0 ? (s.wins / s.total) * 100 : 0;
    });
    
    return stats;
  }, [allTrades]);
  
  // Evaluate expectancy for selected setup and RR
  const expectancyEvaluation = useMemo(() => {
    if (!selectedSetup || !editedData?.rr) {
      return null;
    }
    
    const stats = setupStats[selectedSetup];
    const rr = parseFloat(editedData.rr);
    
    if (!stats || isNaN(rr) || rr <= 0) {
      return null;
    }
    
    // Check minimum sample size
    if (stats.total < 10) {
      return {
        status: 'insufficient_data',
        message: "Not enough data to evaluate this setup yet.",
        setupWinRate: stats.winRate,
        requiredWinRate: null,
        totalTrades: stats.total
      };
    }
    
    // Calculate required win rate: 1 / (1 + RR)
    const requiredWinRate = (1 / (1 + rr)) * 100;
    const winRateDiff = stats.winRate - requiredWinRate;
    
    let status, message;
    
    if (winRateDiff < -5) {
      // Win rate is significantly below required
      status = 'unfavorable';
      message = "This trade may not be profitable based on your historical setup performance.";
    } else if (Math.abs(winRateDiff) <= 5) {
      // Win rate is close to required (within 5%)
      status = 'borderline';
      message = "This trade is close to your setup's historical expectancy.";
    } else {
      // Win rate is above required
      status = 'favorable';
      message = "This trade is statistically aligned with your setup performance.";
    }
    
    return {
      status,
      message,
      setupWinRate: stats.winRate,
      requiredWinRate,
      totalTrades: stats.total
    };
  }, [selectedSetup, editedData?.rr, setupStats]);
  
  // Validate trade levels consistency (Entry, Stop Loss, Take Profit vs Direction)
  const validateTradeLevels = (data) => {
    if (!data) return null;
    
    const entryPrice = parseFloat(data.entry);
    const stopLossPrice = parseFloat(data.stop_loss);
    const takeProfitPrice = parseFloat(data.take_profit);
    const direction = data.direction?.toLowerCase();
    
    // CRITICAL: Direction must be 'long' or 'short'
    if (!direction || (direction !== 'long' && direction !== 'short')) {
      return { field: "direction", message: "Please select a trade direction (Long or Short)." };
    }
    
    // Entry is required
    if (isNaN(entryPrice) || entryPrice <= 0) {
      return { field: "entry", message: "Entry price is required." };
    }
    
    // Stop Loss is required
    if (isNaN(stopLossPrice) || stopLossPrice <= 0) {
      return { field: "stop_loss", message: "Stop loss is required." };
    }
    
    // Take Profit is required
    if (isNaN(takeProfitPrice) || takeProfitPrice <= 0) {
      return { field: "take_profit", message: "Take profit is required." };
    }
    
    // Validate Stop Loss position
    if (direction === "short" && stopLossPrice <= entryPrice) {
      return { field: "stop_loss", message: "For a short trade, stop loss must be above entry." };
    }
    if (direction === "long" && stopLossPrice >= entryPrice) {
      return { field: "stop_loss", message: "For a long trade, stop loss must be below entry." };
    }
    
    // Validate Take Profit position
    if (direction === "short" && takeProfitPrice >= entryPrice) {
      return { field: "take_profit", message: "For a short trade, take profit must be below entry." };
    }
    if (direction === "long" && takeProfitPrice <= entryPrice) {
      return { field: "take_profit", message: "For a long trade, take profit must be above entry." };
    }
    
    return null; // Valid
  };
  
  // Calculate RR ratio from trade levels (non-editable, derived value)
  const calculateRR = (entry, stopLoss, takeProfit, direction) => {
    if (!entry || !stopLoss || !takeProfit || !direction) return null;
    
    const entryVal = parseFloat(entry);
    const slVal = parseFloat(stopLoss);
    const tpVal = parseFloat(takeProfit);
    const dir = direction.toLowerCase();
    
    if (isNaN(entryVal) || isNaN(slVal) || isNaN(tpVal)) return null;
    
    let risk, reward;
    
    if (dir === 'long') {
      risk = entryVal - slVal;
      reward = tpVal - entryVal;
    } else if (dir === 'short') {
      risk = slVal - entryVal;
      reward = entryVal - tpVal;
    } else {
      return null;
    }
    
    if (risk <= 0) return null; // Invalid risk (would be caught by validation)
    
    return reward / risk;
  };
  
  // Auto-calculate RR whenever trade levels change
  useEffect(() => {
    if (editedData) {
      const calculatedRR = calculateRR(
        editedData.entry,
        editedData.stop_loss,
        editedData.take_profit,
        editedData.direction
      );
      
      // Only update if RR has changed (avoid infinite loop)
      if (calculatedRR !== null && calculatedRR !== editedData.rr) {
        setEditedData(prev => ({
          ...prev,
          rr: Math.round(calculatedRR * 100) / 100 // Round to 2 decimal places
        }));
      }
    }
  }, [editedData?.entry, editedData?.stop_loss, editedData?.take_profit, editedData?.direction]);
  
  // Run validation whenever editedData changes
  useEffect(() => {
    if (editedData) {
      const error = validateTradeLevels(editedData);
      setTradeValidationError(error);
    } else {
      setTradeValidationError(null);
    }
  }, [editedData]);
  
  const fileInputRef = useRef(null);
  const assetInputRef = useRef(null);

  const timeframes = ["1M", "5M", "15M", "30M", "1H", "2H", "4H", "6H", "12H", "1D", "1W"];

  useEffect(() => {
    fetchSetups();
    fetchAssetGroups();
    fetchUserSettings();
    fetchTradeCount();
    
    // Private operations - only run when NOT viewing a shared/public profile
    if (!isSharedProfileView) {
      fetchTradeLimitStatus();
      fetchSubscriptionStatus();
      checkPendingPayment();
      checkPendingCryptoPayment();
      applyPendingReferralCode();
    }
  }, [isSharedProfileView]);

  // Apply pending referral code from localStorage (set during signup)
  const applyPendingReferralCode = async () => {
    const refCode = localStorage.getItem('referral_code');
    if (refCode) {
      try {
        await api.post('/referral/set-referrer', { referral_code: refCode });
        localStorage.removeItem('referral_code');
      } catch (error) {
        // Silently fail - code may be invalid or already set
        console.log('Could not apply referral code:', error.response?.data?.detail);
        localStorage.removeItem('referral_code');
      }
    }
  };

  // Pre-fill hand size when lastHandSize is loaded (only on initial load)
  useEffect(() => {
    if (lastHandSize && !handSizeEdited && !handSize) {
      setHandSize(lastHandSize.toString());
    }
  }, [lastHandSize]);

  // Check alerts when trades and capital are loaded - NOT in public profile mode
  useEffect(() => {
    if (!isSharedProfileView && !tradesLoading && allTrades.length > 0) {
      (async () => {
        await syncAlertSettingsFromBackend();
        checkAlerts({
          trades: allTrades,
          capital: userCapital,
          referenceCapital: userCapital,
          currentCapital: currentCapital
        });
      })();
    }
  }, [isSharedProfileView, tradesLoading, allTrades, userCapital, currentCapital, checkAlerts]);

  const fetchTradeLimitStatus = async () => {
    try {
      const response = await api.get('/user/trade-limit-status');
      setTradeLimitStatus(response.data);
      setTradeCount(response.data.trade_count);
    } catch (error) {
      console.error("Error fetching trade limit status:", error);
    }
  };

  const checkPendingCryptoPayment = async () => {
    try {
      const response = await api.get('/payments/pending-crypto');
      if (response.data.has_pending && response.data.payment) {
        setPendingCryptoPayment(response.data.payment);
      } else {
        setPendingCryptoPayment(null);
      }
    } catch (error) {
      console.error("Error checking pending crypto payment:", error);
      setPendingCryptoPayment(null);
    }
  };

  // Check for Stripe payment redirect and poll status
  const checkStripePaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const stripeSessionId = urlParams.get('stripe_session_id');
    const paymentStatus = urlParams.get('payment_status');
    
    console.log('[Stripe] Checking payment status:', { stripeSessionId, paymentStatus, url: window.location.href });
    
    if (!stripeSessionId || paymentStatus !== 'success') {
      return; // Not a Stripe redirect
    }
    
    // Clear URL params to prevent re-processing on refresh
    window.history.replaceState({}, document.title, window.location.pathname);
    
    console.log('[Stripe] Payment success detected, polling status for session:', stripeSessionId);
    
    try {
      // Poll Stripe status to confirm payment
      toast.loading('Verifying payment...', { id: 'stripe-verify' });
      
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;
      
      const pollStatus = async () => {
        try {
          console.log('[Stripe] Polling attempt:', attempts + 1);
          const response = await api.get(`/payments/stripe/status/${stripeSessionId}`);
          console.log('[Stripe] Status response:', response.data);
          
          if (response.data.payment_status === 'paid') {
            toast.success('Payment successful! Welcome to PRO!', { id: 'stripe-verify' });
            // Refresh subscription status
            await fetchSubscriptionStatus();
            await fetchTradeLimitStatus();
            return true;
          } else if (response.data.status === 'expired') {
            toast.error('Payment session expired. Please try again.', { id: 'stripe-verify' });
            return true;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            return pollStatus();
          } else {
            // After 5 attempts, check the DB directly via verify-pending
            const verifyResponse = await api.post('/payments/stripe/verify-pending');
            if (verifyResponse.data.verified) {
              toast.success(verifyResponse.data.message || 'Payment verified! Welcome to PRO!', { id: 'stripe-verify' });
              await fetchSubscriptionStatus();
              await fetchTradeLimitStatus();
            } else {
              toast.success('Payment is being processed. You will be upgraded shortly.', { id: 'stripe-verify' });
            }
            return true;
          }
        } catch (error) {
          console.error('[Stripe] Error checking status:', error);
          
          // If 401, auth might not be ready - wait and retry
          if (error.response?.status === 401 && attempts < maxAttempts) {
            attempts++;
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            return pollStatus();
          }
          
          toast.error('Could not verify payment status. Please refresh the page.', { id: 'stripe-verify' });
          return true;
        }
      };
      
      await pollStatus();
    } catch (error) {
      console.error('[Stripe] Error in payment verification:', error);
    }
  };

  // Run Stripe payment check on mount
  useEffect(() => {
    if (!isSharedProfileView) {
      const checkPayments = async () => {
        // Check URL params first
        const urlParams = new URLSearchParams(window.location.search);
        const stripeSessionId = urlParams.get('stripe_session_id');
        const paymentStatus = urlParams.get('payment_status');
        
        if (stripeSessionId && paymentStatus === 'success') {
          // Give auth a moment to initialize after redirect
          await new Promise(resolve => setTimeout(resolve, 1000));
          await checkStripePaymentStatus();
        } else if (paymentStatus === 'canceled') {
          window.history.replaceState({}, document.title, window.location.pathname);
          toast.info('Payment was canceled.');
        }
        
        // Also try verify-pending as fallback (delayed to ensure auth)
        await new Promise(resolve => setTimeout(resolve, 1500));
        verifyPendingStripePayments();
      };
      
      checkPayments();
    }
  }, []);

  // Verify any pending Stripe payments (fallback mechanism)
  const verifyPendingStripePayments = async () => {
    try {
      const response = await api.post('/payments/stripe/verify-pending');
      console.log('[Stripe] Verify pending response:', response.data);
      
      if (response.data.verified) {
        toast.success(response.data.message || 'Payment verified! Welcome to PRO!');
        // Refresh subscription status
        await fetchSubscriptionStatus();
        await fetchTradeLimitStatus();
      }
    } catch (error) {
      // Silently fail - this is just a fallback check
      console.log('[Stripe] No pending payments to verify or error:', error.response?.data?.detail || error.message);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await api.get('/settings');
      if (response.data.last_hand_size) {
        setLastHandSize(response.data.last_hand_size);
        // Also try localStorage as additional persistence
        localStorage.setItem('last_hand_size', response.data.last_hand_size.toString());
      } else {
        // Fallback to localStorage
        const localHandSize = localStorage.getItem('last_hand_size');
        if (localHandSize) {
          setLastHandSize(parseFloat(localHandSize));
        }
      }
      // Fetch initial capital for alert calculations
      if (response.data.initial_capital) {
        setUserCapital(response.data.initial_capital);
      }
      
      // Fetch current capital from performance endpoint for capital drop alert
      try {
        const perfResponse = await api.get('/performance');
        if (perfResponse.data.current_capital !== undefined) {
          setCurrentCapital(perfResponse.data.current_capital);
        }
      } catch (perfError) {
        console.error("Error fetching performance metrics:", perfError);
      }
    } catch (error) {
      console.error("Error fetching user settings:", error);
      // Fallback to localStorage if API fails
      const localHandSize = localStorage.getItem('last_hand_size');
      if (localHandSize) {
        setLastHandSize(parseFloat(localHandSize));
      }
    }
  };

  const fetchSetups = async () => {
    try {
      const response = await api.get('/setups');
      const fetchedSetups = response.data;
      setSetups(fetchedSetups);
      setSetupsError(false);
      // Do NOT auto-select any setup - user must explicitly choose
    } catch (error) {
      console.error("Error fetching setups:", error);
      // Do NOT clear `setups` here - a load failure is not the same as "user has
      // no setups registered". Keep whatever was last loaded and surface the error.
      setSetupsError(true);
    }
  };

  // Create Setup handler (same as Analytics)
  const handleCreateSetup = async () => {
    if (!newSetupName.trim()) {
      toast.error("Please enter a setup name");
      return;
    }

    setCreatingSetup(true);
    try {
      const response = await api.post('/setups', {
        name: newSetupName,
        description: newSetupDescription
      });
      
      toast.success("Setup created successfully");
      
      // Get the new setup ID from response
      const newSetup = response.data;
      
      // Reset form
      setNewSetupName("");
      setNewSetupDescription("");
      setShowCreateSetupModal(false);
      
      // Refresh setups list
      await fetchSetups();
      
      // Auto-select the newly created setup
      if (newSetup && newSetup.id) {
        setSelectedSetup(newSetup.id);
      }
    } catch (error) {
      console.error("Error creating setup:", error);
      toast.error("Failed to create setup");
    } finally {
      setCreatingSetup(false);
    }
  };

  // Handle setup dropdown change with special "+ Create setup" action
  const handleSetupChange = (e) => {
    const value = e.target.value;
    if (value === "__create_new__") {
      // Open create setup modal instead of setting value
      setShowCreateSetupModal(true);
      // Reset dropdown to previous value (or empty)
      e.target.value = selectedSetup;
    } else {
      setSelectedSetup(value);
    }
  };

  const fetchAssetGroups = async () => {
    try {
      const response = await api.get('/asset-groups');
      setAssetGroups(response.data);
    } catch (error) {
      console.error("Error fetching asset groups:", error);
    }
  };

  const fetchTradeCount = async () => {
    setTradesLoading(true);
    try {
      const response = await api.get('/trades');
      const trades = response.data;
      setAllTrades(trades);
      setTradeCount(trades.length);
    } catch (error) {
      console.error("Error fetching trades:", error);
      // Preservar os trades já carregados na tela em caso de falha de conexão ou timeout da API,
      // em vez de limpar silenciosamente os dados e fingir que o usuário não possui trades
    } finally {
      setTradesLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await api.get('/user/profile');
      const profile = response.data;
      
      // Store user email for admin checks
      if (profile.email) {
        setUserEmail(profile.email);
      }
      
      let newStatus = 'free';
      
      // Check if user has active Pro subscription
      if (profile.subscription_status === 'pro') {
        // Verify subscription hasn't expired
        if (profile.subscription_expires_at) {
          const expiresAt = new Date(profile.subscription_expires_at);
          if (expiresAt > new Date()) {
            newStatus = 'pro';
          }
        }
      }
      
      // Detect status change from free to pro (upgrade success)
      // Use backend flag to track if user has seen the Pro welcome popup
      const proWelcomeSeen = profile.pro_welcome_seen === true;
      
      // Load confidence tracking preference
      const confTrackingEnabled = profile.confidence_tracking_enabled !== false; // Default to true
      setConfidenceTrackingEnabled(confTrackingEnabled);
      // Sync to localStorage for instant initialization on next load
      try {
        localStorage.setItem('geni_confidence_tracking_enabled', confTrackingEnabled.toString());
      } catch (e) {
        console.error('Error saving confidence tracking preference to localStorage:', e);
      }
      
      // Load hasConfirmedDoNotTrackConfidence from backend (persisted per user)
      const confirmedDoNotTrack = profile.has_confirmed_do_not_track_confidence === true;
      setHasConfirmedDoNotTrackConfidence(confirmedDoNotTrack);
      try {
        localStorage.setItem('hasConfirmedDoNotTrackConfidence', confirmedDoNotTrack.toString());
      } catch (e) {
        console.error('Error saving hasConfirmedDoNotTrackConfidence to localStorage:', e);
      }
      
      // Show success modal ONLY if:
      // 1. User is currently Pro
      // 2. User has NOT seen the Pro welcome popup yet (backend flag)
      if (newStatus === 'pro' && !proWelcomeSeen) {
        setShowProSuccessModal(true);
      }
      
      // Update status
      setSubscriptionStatus(newStatus);
      
    } catch (error) {
      console.error("Error fetching subscription status:", error);
      setSubscriptionStatus('free');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const checkPendingPayment = async () => {
    try {
      const response = await api.get('/payments/my');
      const payments = response.data;
      
      // Check if user has any pending payments
      const pendingPayment = payments.find(p => p.status === 'pending');
      setHasPendingPayment(!!pendingPayment);
      
      // Note: We no longer auto-show the pending modal on page load
      // The "Request Received" modal should only appear immediately after payment submission
      // The pending status is still visible via the navigation indicator
      
      // Clear pending modal flag if no more pending payments (for cleanup)
      if (!pendingPayment) {
        sessionStorage.removeItem('geni_pending_modal_shown');
      }
    } catch (error) {
      console.error("Error checking pending payment:", error);
      setHasPendingPayment(false);
    }
  };

  // User is Pro only if subscription_status is 'pro' AND not expired
  const isPro = subscriptionStatus === 'pro';
  
  // Generate insights from trade data (memoized)
  const insightsData = useMemo(() => {
    if (tradesLoading || allTrades.length < INSIGHTS_MIN_TRADES) {
      return { insights: [], hasLowQualityData: false };
    }
    return generateInsights(allTrades, setups);
  }, [allTrades, tradesLoading, setups]);
  
  const insights = insightsData.insights;
  const allInsights = insightsData.allInsights || [];
  const hasLowQualityData = insightsData.hasLowQualityData;
  
  // Get progress state for momentum section (memoized)
  const progressState = useMemo(() => {
    if (tradesLoading) {
      return null;
    }
    return getProgressState(allTrades);
  }, [allTrades, tradesLoading]);
  
  // Generate insights hash for comparison (to detect new insights)
  const insightsHash = useMemo(() => {
    if (insights.length === 0) return '';
    return insights.map(i => i.text).sort().join('|');
  }, [insights]);
  
  // Auto-expand and track unseen insights
  useEffect(() => {
    if (tradesLoading || insights.length === 0) {
      setHasUnseenInsights(false);
      return;
    }
    
    const seenHash = localStorage.getItem('geni_seen_insights_hash');
    
    if (seenHash !== insightsHash) {
      // New insights detected
      setHasUnseenInsights(true);
      setInsightsExpanded(true); // Auto-expand
    } else {
      setHasUnseenInsights(false);
    }
  }, [insightsHash, tradesLoading, insights.length]);
  
  // Mark insights as seen when section is expanded
  useEffect(() => {
    if (insightsExpanded && insights.length > 0 && insightsHash) {
      localStorage.setItem('geni_seen_insights_hash', insightsHash);
      // Small delay to keep the "New" badge visible briefly
      const timer = setTimeout(() => {
        setHasUnseenInsights(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [insightsExpanded, insights.length, insightsHash]);
  
  // Use trade limit status for accurate limit checking (includes bonus)
  const effectiveLimit = tradeLimitStatus?.effective_limit || FREE_TRADE_LIMIT;
  const isAtTradeLimit = false; // Always false to remove 10-trade limit for FREE users
  const canCreateTrade = true;  // Always true to allow FREE users to log >10 trades
  const bonusGranted = tradeLimitStatus?.bonus_granted || false;
  const bonusRemaining = tradeLimitStatus?.bonus_remaining || 0;
  const hasBonusTrades = bonusGranted && bonusRemaining > 0;
  
  // Banner logic:
  // 1. Don't show any banner while subscription is still loading (prevents flash)
  // 2. If pending payment + bonus remaining: show "Payment pending · X bonus trades left"
  // 3. If pending payment + bonus exhausted: show "Payment pending · waiting for approval"
  // 4. If no pending payment + at limit: show normal upgrade banner
  // IMPORTANT: Only evaluate banner visibility AFTER subscription data is loaded
  const showPendingBanner = !subscriptionLoading && hasPendingPayment && bonusGranted && !isPro;
  const showLimitBanner = !subscriptionLoading && isAtTradeLimit && !isPro && !hasPendingPayment;

  // Open crypto modal with selected plan (crypto-only flow)
  const handleUpgradeClick = (plan) => {
    setCryptoModalPlan(plan);
    setShowUpgradeModal(false);
    setShowCryptoModal(true);
  };

  const handleOpenCryptoModal = (plan = null) => {
    setCryptoModalPlan(plan);
    setShowUpgradeModal(false);
    setShowCryptoModal(true);
  };

  const handleCryptoPaymentSubmitted = (paymentData) => {
    // Update states to reflect pending crypto payment
    setHasPendingPayment(true);
    setPendingCryptoPayment(paymentData);
    
    // Refresh trade limit status to get updated bonus info
    fetchTradeLimitStatus();
    
    // Clear session flag so pending modal can show
    sessionStorage.removeItem('geni_pending_modal_shown');
  };

  const processImageFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }
    
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    setAnalysisResult(null);
    setEditMode(false);
    setShowPasteHint(false);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        if (blob) {
          processImageFile(blob);
          toast.success("Image pasted successfully!");
        }
        break;
      }
    }
  };

  React.useEffect(() => {
    window.addEventListener("paste", handlePaste);
    
    const handleFocus = () => setShowPasteHint(true);
    const handleBlur = () => setTimeout(() => setShowPasteHint(false), 200);
    
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("paste", handlePaste);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  const handleAnalyze = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedImage);

      const response = await api.post('/analyze', formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setAnalysisResult(response.data);
      
      // Set empty notes and null confidence for user selection
      // Confidence defaults to null = "Do not track this" for minimal friction
      const cleanedData = {
        ...response.data,
        notes: "",  // Always start with empty notes for user input
        confidence: null  // Default to "Do not track" for fewer clicks
      };
      setEditedData(cleanedData);
      
      // Auto-fill timeframe if detected
      if (response.data.detected_timeframe) {
        setSelectedTimeframe(response.data.detected_timeframe);
        setTimeframeDetected(true);
        setTimeframeConfidence(response.data.timeframe_confidence || "medium");
      } else {
        setTimeframeDetected(false);
        setTimeframeConfidence("none");
      }
      
      // Auto-match asset to asset group
      if (response.data.asset) {
        const matchedGroup = assetGroups.find(group => 
          group.assets.some(asset => asset.toUpperCase() === response.data.asset.toUpperCase())
        );
        
        if (matchedGroup) {
          setSelectedAssetGroup(matchedGroup.id);
          setAssetGroupDetected(true);
          toast.success(`Chart analyzed! Matched to "${matchedGroup.name}" group.`);
        } else {
          setAssetGroupDetected(false);
          toast.success("Chart analyzed successfully!");
        }
      } else {
        toast.success("Chart analyzed successfully!");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || "";
      
      // Handle different error types with user-friendly messages
      if (status === 503) {
        // Service temporarily unavailable
        toast.error("Chart analysis is temporarily unavailable. You can still enter the trade manually.", {
          duration: 5000
        });
      } else if (status === 400) {
        // Invalid image format or size
        toast.error(errorMsg || "Invalid image. Please upload a clear chart screenshot (PNG, JPG, or WEBP).");
      } else if (status === 422) {
        // Image couldn't be analyzed
        toast.error("We couldn't analyze this image. Please ensure it shows a clear trading chart, or enter the trade manually.", {
          duration: 5000
        });
      } else {
        // Generic error - don't expose raw provider errors
        const safeErrorPatterns = ['API key', 'authentication', 'litellm', 'RateLimitError', 'RESOURCE_EXHAUSTED'];
        const isSensitiveError = safeErrorPatterns.some(pattern => 
          errorMsg.toLowerCase().includes(pattern.toLowerCase())
        );
        
        if (isSensitiveError || !errorMsg) {
          toast.error("We couldn't analyze this image automatically. You can still enter the trade manually.", {
            duration: 5000
          });
        } else {
          toast.error(errorMsg);
        }
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    // Check trade limit before saving - use canCreateTrade which includes bonus
    if (!canCreateTrade && !isPro) {
      // User has exhausted their trades - show upgrade modal
      // Don't show the pending modal here - it's intrusive during normal usage
      // Instead, show a toast and the upgrade modal so they know the status
      if (hasPendingPayment) {
        toast.info("Your payment is pending review. Upgrade to Pro for unlimited trades.");
        setShowUpgradeModal(true);
      } else {
        setShowUpgradeModal(true);
      }
      return;
    }

    // Asset/symbol must be identified - either by AI or manually. Never invent one.
    // Applies regardless of manual mode, since AI analysis can also fail to detect it.
    if (!editedData?.asset || editedData.asset.trim() === '') {
      toast.error("Please enter the asset/symbol before saving the trade.");
      setEditMode(true);
      setTimeout(() => assetInputRef.current?.focus(), 100);
      return;
    }

    // Validate required fields for manual mode
    if (isManualMode) {
      if (!editedData?.direction) {
        toast.error("Please select a direction");
        return;
      }
    }

    if (!selectedSetup) {
      toast.error("Please select a setup before saving");
      return;
    }

    if (!selectedTimeframe) {
      toast.error("Please select a timeframe before saving");
      return;
    }

    // Validate trade levels consistency (Entry, Stop Loss, Take Profit) using a FRESH
    // check against the current editedData, not the `tradeValidationError` state - that
    // state is set by a useEffect and can lag one render behind a just-made selection
    // (e.g. picking Direction and clicking Save right after), causing a stale "direction
    // missing" error even though editedData.direction is already correctly set.
    const freshValidationError = validateTradeLevels(editedData);
    if (freshValidationError) {
      toast.error(freshValidationError.message);
      return;
    }

    // Confidence validation removed - default is now "Do not track" for minimal friction
    // Users can optionally select High/Medium/Low if they want to track confidence
    // Clear any lingering confidence error
    setConfidenceError(null);

    // Check for unfavorable expectancy - show confirmation modal
    if (expectancyEvaluation?.status === 'unfavorable' && !pendingSaveData) {
      setPendingSaveData(true); // Mark that we're waiting for confirmation
      setShowExpectancyWarning(true);
      return;
    }

    setSaving(true);
    try {
      const handSizeValue = handSize ? parseFloat(handSize) : null;
      
      // Determine status and outcome based on selected result
      // PENDING → status="open", outcome="pending"
      // GAIN → status="closed", outcome="win"
      // LOSS → status="closed", outcome="loss"
      let tradeStatus = "open";
      let tradeOutcome = "pending";
      let profitUsd = null;
      let finalRR = editedData.rr || null;
      
      if (selectedOutcome === "win") {
        tradeStatus = "closed";
        tradeOutcome = "win";
        // Calculate profit for winning trade: hand_size * RR
        if (handSizeValue && finalRR) {
          profitUsd = Math.round(handSizeValue * Math.abs(finalRR) * 100) / 100;
        }
      } else if (selectedOutcome === "loss") {
        tradeStatus = "closed";
        tradeOutcome = "loss";
        // Calculate profit for losing trade: -hand_size * 1 (1R loss)
        // For loss, RR should be negative or we use -1R
        if (handSizeValue) {
          // If RR is provided and negative, use it; otherwise assume -1R loss
          const lossRR = finalRR < 0 ? finalRR : -1;
          profitUsd = Math.round(handSizeValue * lossRR * 100) / 100;
          finalRR = lossRR;
        }
      }
      
      const tradeData = {
        ...editedData,
        setup_id: selectedSetup,
        asset_group_id: selectedAssetGroup || null,
        status: tradeStatus,
        position_status: tradeStatus === "open" ? positionStatus : "active", // Only relevant for open trades
        outcome: tradeOutcome,
        timeframe: selectedTimeframe,
        hand_size: handSizeValue,
        // Defensive coercion: entry/stop_loss/take_profit can still hold a raw
        // string (e.g. locale decimal comma) if set outside the number input's
        // onChange. Backend requires strict floats and rejects "0,98767".
        entry: toSafeNumber(editedData.entry),
        stop_loss: toSafeNumber(editedData.stop_loss),
        take_profit: toSafeNumber(editedData.take_profit),
        asset: editedData.asset.trim().toUpperCase(),
        rr: finalRR,
        profit_usd: profitUsd,
        profit_loss: null
      };

      await api.post('/trades', tradeData);
      
      if (tradeStatus === "closed") {
        toast.success(`Trade saved as ${tradeOutcome.toUpperCase()}! ${profitUsd !== null ? (profitUsd >= 0 ? '+' : '') + '$' + profitUsd.toFixed(2) : ''}`);
      } else {
        toast.success("Trade saved to Open Positions!");
      }
      
      // Update trade count and refresh limit status
      setTradeCount(prev => prev + 1);
      fetchTradeLimitStatus(); // Refresh to get updated bonus usage
      
      // Update localStorage immediately for faster pre-fill on next trade
      if (handSizeValue) {
        localStorage.setItem('last_hand_size', handSizeValue.toString());
        setLastHandSize(handSizeValue);
      }
      
      // Reset form but keep last_hand_size for next trade
      setSelectedImage(null);
      setImagePreview(null);
      setAnalysisResult(null);
      setEditMode(false);
      setIsManualMode(false); // Reset manual mode
      setSelectedSetup("");
      setSelectedAssetGroup("");
      setAssetGroupDetected(false);
      setSelectedOutcome("pending");
      setPositionStatus("active"); // Reset position status for next trade
      setSelectedTimeframe("");
      setTimeframeDetected(false);
      setTimeframeConfidence("");
      // Pre-fill hand size with last used value for next trade
      setHandSize(handSizeValue ? handSizeValue.toString() : "");
      setHandSizeEdited(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
      setPendingSaveData(null); // Reset pending save state
    }
  };

  // Handle confirmed save after expectancy warning
  const handleConfirmedSave = () => {
    setShowExpectancyWarning(false);
    // pendingSaveData is still true, so handleSave will proceed
    handleSave();
  };

  // Handle cancel from expectancy warning
  const handleCancelExpectancyWarning = () => {
    setShowExpectancyWarning(false);
    setPendingSaveData(null);
  };

  const handleReset = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResult(null);
    setEditMode(false);
    setTimeframeDetected(false);
    setTimeframeConfidence("");
    setIsManualMode(false); // Reset manual mode
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Start manual trade entry (no image required)
  const handleStartManualMode = () => {
    setIsManualMode(true);
    setEditMode(true); // Enable edit mode for manual entry
    setAnalysisResult(null);
    setSelectedImage(null);
    setImagePreview(null);
    
    // Initialize empty trade data with defaults
    setEditedData({
      asset: '',
      direction: 'long',
      entry: '',
      stop_loss: '',
      take_profit: '',
      rr: null,
      trade_status: 'open',
      notes: '',
      confidence: null, // Default to "Do not track"
      detected_timeframe: null,
      timeframe_confidence: 'none'
    });
    
    // Reset other form state
    setSelectedOutcome('pending');
    setSelectedSetup('');
    setSelectedAssetGroup('');
    setAssetGroupDetected(false);
    setSelectedTimeframe('');
    setTimeframeDetected(false);
    setTimeframeConfidence('');
    setHandSize('');
    setHandSizeEdited(false);
  };

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className={`${isSharedProfileView ? '' : 'min-h-[calc(100vh-64px)]'} bg-[#0A0A0A] p-4 md:p-8`} data-testid="dashboard-page">
      <div className="w-full max-w-[1600px] mx-auto">
        {/* Header Section - HIDDEN in shared/public profile (shown in PublicProfileWrapper) */}
        {!isSharedProfileView && (
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-2" data-testid="dashboard-title">
              Chart Analysis
            </h2>
            <p className="text-white/60 text-base font-sans">
              {t('dashboard.uploadToExtract')}
            </p>
          </div>
        </div>
        )}

        {/* Trade Entry Section - Hidden in shared profile mode */}
        {!isSharedProfileView && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px md:gap-6">
          {/* Upload Area */}
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6" data-testid="upload-section">
            <div className="mb-4">
              <h3 className="text-xl font-bold font-heading mb-2">{t('dashboard.uploadChart')}</h3>
              <p className="text-sm text-white/60">JPG, PNG, or WEBP format</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              id="file-upload"
              data-testid="file-input"
            />
            
            {!imagePreview ? (
              <>
                <label
                  htmlFor="file-upload"
                  data-testid="upload-area"
                  className="border-2 border-dashed border-white/10 rounded-sm p-12 flex flex-col items-center justify-center cursor-pointer hover:border-white/20 transition-colors min-h-[400px] relative"
                >
                  {!showPasteHint ? (
                    <>
                      <Upload size={48} weight="duotone" className="text-white/40 mb-4" />
                      <p className="text-white/60 text-center mb-3">{language === 'ptBR' ? 'Clique para enviar gráfico de trading' : 'Click to upload trading chart'}</p>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-px bg-white/10 w-16"></div>
                        <span className="text-white/40 text-xs uppercase tracking-wider">{t('common.or')}</span>
                        <div className="h-px bg-white/10 w-16"></div>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-sm px-4 py-2 flex items-center gap-2">
                        <kbd className="bg-[#0A0A0A] border border-white/20 rounded px-2 py-1 text-xs font-mono text-white">Ctrl</kbd>
                        <span className="text-white/40 text-xs">+</span>
                        <kbd className="bg-[#0A0A0A] border border-white/20 rounded px-2 py-1 text-xs font-mono text-white">V</kbd>
                        <span className="text-white/60 text-xs ml-2">{language === 'ptBR' ? 'Colar da área de transferência' : 'Paste from clipboard'}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center p-6 bg-[#00E676]/10 border border-[#00E676]/40 rounded-sm px-6 py-3">
                      <p className="text-[#00E676] font-bold text-sm">{language === 'ptBR' ? 'Pronto para colar! Pressione Ctrl+V' : 'Ready to paste! Press Ctrl+V'}</p>
                    </div>
                  )}
                </label>
                
                {/* Manual trade entry option */}
                <div className="mt-4 text-center">
                  <button
                    onClick={handleStartManualMode}
                    className="text-white/40 hover:text-white/70 text-sm transition-colors inline-flex items-center gap-2"
                    data-testid="manual-trade-btn"
                  >
                    <PencilSimpleLine size={16} weight="duotone" />
                    {language === 'ptBR' ? 'Adicionar trade sem imagem' : 'Add trade without image'}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4" data-testid="image-preview-section">
                <div className="relative border border-white/10 rounded-sm overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Chart preview"
                    className="w-full h-auto"
                    data-testid="chart-preview-image"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    data-testid="analyze-button"
                    className="flex-1 bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm px-6 py-2"
                  >
                    {analyzing ? (
                      <>
                        <ArrowsClockwise size={20} weight="bold" className="mr-2 animate-spin" />
                        {language === 'ptBR' ? 'Analisando...' : 'Analyzing...'}
                      </>
                    ) : (
                      language === 'ptBR' ? 'Analisar Gráfico' : 'Analyze Chart'
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleReset}
                    data-testid="reset-button"
                    variant="outline"
                    className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-sm"
                  >
                    <Trash size={20} weight="duotone" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Analysis Results */}
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6" data-testid="trade-result-section">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold font-heading">{language === 'ptBR' ? 'Resultado do Trade' : 'Trade Result'}</h3>
              {(analysisResult || isManualMode) && (
                <div className="flex items-center gap-2">
                  {/* Only show Edit/View toggle when NOT in manual mode (manual is always edit mode) */}
                  {!isManualMode && (
                    <Button
                      onClick={() => setEditMode(!editMode)}
                      data-testid="edit-toggle-button"
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-sm text-xs"
                    >
                      {editMode ? (language === 'ptBR' ? 'Modo Visualização' : 'View Mode') : (language === 'ptBR' ? 'Modo Edição' : 'Edit Mode')}
                    </Button>
                  )}
                  <Button
                    onClick={handleReset}
                    data-testid="cancel-analysis-button"
                    variant="outline"
                    size="sm"
                    className="border-white/10 text-[#FF3B30]/70 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm text-xs"
                  >
                    <X size={14} weight="bold" className="mr-1" />
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
            </div>

            {!analysisResult && !isManualMode ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-white/40" data-testid="no-analysis-placeholder">
                <ImageIcon size={48} weight="duotone" className="mb-4" />
                <p className="text-center">{t('dashboard.uploadAndAnalyze')}</p>
              </div>
            ) : editMode ? (
              <div className="space-y-4" data-testid="edit-mode-section">
                {/* Manual mode indicator */}
                {isManualMode && (
                  <div className="bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm px-3 py-2 flex items-center gap-2 mb-2">
                    <PencilSimpleLine size={14} weight="duotone" className="text-[#00B8D4]" />
                    <span className="text-[#00B8D4]/80 text-xs font-mono">{language === 'ptBR' ? 'Entrada manual (sem análise de gráfico)' : 'Manual trade entry (no chart analysis)'}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Trade Status</label>
                    <select
                      value={editedData.trade_status}
                      onChange={(e) => handleFieldChange("trade_status", e.target.value)}
                      data-testid="edit-trade-status"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
                    >
                      <option value="completed">Completed</option>
                      <option value="setup">Setup</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Direction</label>
                    <select
                      value={editedData.direction}
                      onChange={(e) => handleFieldChange("direction", e.target.value)}
                      data-testid="edit-direction"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
                    >
                      <option value="long">Long</option>
                      <option value="short">Short</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Asset</label>
                  <input
                    type="text"
                    value={editedData.asset}
                    onChange={(e) => handleFieldChange("asset", e.target.value)}
                    data-testid="edit-asset"
                    ref={assetInputRef}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Entry</label>
                    <input
                      type="number"
                      step="any"
                      value={editedData.entry}
                      onChange={(e) => handleFieldChange("entry", parseFloat(e.target.value))}
                      data-testid="edit-entry"
                      className={`w-full bg-[#0A0A0A] text-white rounded-sm px-3 py-2 text-sm font-mono focus:outline-none ${
                        tradeValidationError 
                          ? 'border border-[#FF3B30]/50 focus:border-[#FF3B30]' 
                          : 'border border-white/10 focus:border-white'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Stop Loss</label>
                    <input
                      type="number"
                      step="any"
                      value={editedData.stop_loss}
                      onChange={(e) => handleFieldChange("stop_loss", parseFloat(e.target.value))}
                      data-testid="edit-stop-loss"
                      className={`w-full bg-[#0A0A0A] text-white rounded-sm px-3 py-2 text-sm font-mono focus:outline-none ${
                        tradeValidationError?.field === 'stop_loss' 
                          ? 'border border-[#FF3B30]/50 focus:border-[#FF3B30]' 
                          : 'border border-white/10 focus:border-white'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Take Profit</label>
                    <input
                      type="number"
                      step="any"
                      value={editedData.take_profit}
                      onChange={(e) => handleFieldChange("take_profit", parseFloat(e.target.value))}
                      data-testid="edit-take-profit"
                      className={`w-full bg-[#0A0A0A] text-white rounded-sm px-3 py-2 text-sm font-mono focus:outline-none ${
                        tradeValidationError?.field === 'take_profit' 
                          ? 'border border-[#FF3B30]/50 focus:border-[#FF3B30]' 
                          : 'border border-white/10 focus:border-white'
                      }`}
                    />
                  </div>
                </div>

                {/* Trade Validation Error */}
                {tradeValidationError && (
                  <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm px-3 py-2 flex items-center gap-2" data-testid="trade-validation-error">
                    <X size={16} className="text-[#FF3B30] flex-shrink-0" />
                    <span className="text-[#FF3B30] text-sm">{tradeValidationError.message}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">RR Ratio (Auto)</label>
                    <div 
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono"
                      data-testid="edit-rr"
                    >
                      {editedData.rr != null && !isNaN(editedData.rr) 
                        ? `${editedData.rr.toFixed(2)}R` 
                        : <span className="text-white/40">-</span>
                      }
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Confidence</label>
                    <select
                      value={
                        editedData.confidence || '__not_tracked__'
                      }
                      onChange={async (e) => {
                        const value = e.target.value;
                        // Clear error when user interacts
                        setConfidenceError(null);
                        
                        if (value === '__not_tracked__') {
                          // Show warning modal only on first manual selection (not on default)
                          if (!hasConfirmedDoNotTrackConfidence && editedData.confidence !== null) {
                            setPendingConfidenceDisable(true);
                            setShowConfidenceDisableModal(true);
                          } else {
                            // Already confirmed or re-selecting after initial default
                            handleFieldChange("confidence", null);
                          }
                        } else if (value !== '') {
                          handleFieldChange("confidence", value);
                        }
                      }}
                      data-testid="edit-confidence"
                      className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2 text-sm font-mono focus:outline-none ${
                        confidenceError ? 'border-[#FF3B30]' : 'border-white/10 focus:border-white'
                      } ${
                        editedData.confidence === 'high' ? 'text-[#00E676]' :
                        editedData.confidence === 'medium' ? 'text-[#FFA726]' :
                        editedData.confidence === 'low' ? 'text-[#FF3B30]' :
                        'text-white/50'
                      }`}
                    >
                      <option value="high" className="text-[#00E676]">High</option>
                      <option value="medium" className="text-[#FFA726]">Medium</option>
                      <option value="low" className="text-[#FF3B30]">Low</option>
                      <option value="__not_tracked__" className="text-white/50">Do not track this</option>
                    </select>
                    {confidenceError && (
                      <div className="text-[#FF3B30] text-[10px] mt-1 font-mono">
                        {confidenceError}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Your Notes (Optional)</label>
                  <textarea
                    value={editedData.notes}
                    onChange={(e) => handleFieldChange("notes", e.target.value)}
                    data-testid="edit-notes"
                    placeholder="Add your trade notes here..."
                    rows={4}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none resize-none placeholder:text-white/30"
                  />
                </div>

                {/* Setup Selection in Edit Mode */}
                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Setup *</label>
                  <select
                    value={selectedSetup}
                    onChange={handleSetupChange}
                    data-testid="edit-setup-select"
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
                  >
                    <option value="">Select a setup</option>
                    {setups.map((setup) => (
                      <option key={setup.id} value={setup.id}>
                        {setup.name}
                      </option>
                    ))}
                    <option value="__create_new__" className="text-[#00E676]">+ Create setup</option>
                  </select>
                  {setupsError && (
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-[#F59E0B]" data-testid="edit-setups-error">
                      <Warning size={12} weight="fill" />
                      <span>Failed to load your setups.</span>
                      <button
                        type="button"
                        onClick={fetchSetups}
                        data-testid="retry-edit-setups-btn"
                        className="underline hover:text-[#F59E0B]/80"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                </div>

                {/* Asset Group Selection in Edit Mode */}
                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Asset Group (Optional)</label>
                  <select
                    value={selectedAssetGroup}
                    onChange={(e) => {
                      setSelectedAssetGroup(e.target.value);
                      if (assetGroupDetected) {
                        setAssetGroupDetected(false);
                      }
                    }}
                    data-testid="edit-asset-group-select"
                    className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none ${
                      assetGroupDetected 
                        ? 'border-[#00E676]/30 text-white' 
                        : 'border-white/10 text-white'
                    }`}
                  >
                    <option value="">No group</option>
                    {assetGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Timeframe Selection in Edit Mode */}
                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Timeframe *</label>
                  <select
                    value={selectedTimeframe}
                    onChange={(e) => {
                      setSelectedTimeframe(e.target.value);
                      if (timeframeDetected) {
                        setTimeframeDetected(false);
                      }
                    }}
                    data-testid="edit-timeframe-select"
                    className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none ${
                      timeframeDetected 
                        ? 'border-[#00E676]/30 text-white' 
                        : 'border-white/10 text-white'
                    }`}
                  >
                    <option value="">Choose timeframe...</option>
                    {timeframes.map((tf) => (
                      <option key={tf} value={tf}>
                        {tf}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Hand Size in Edit Mode */}
                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Hand Size (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={handSize}
                    onChange={(e) => {
                      setHandSize(e.target.value);
                      setHandSizeEdited(true);
                    }}
                    data-testid="edit-hand-size-input"
                    placeholder="Enter position size in USD"
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none placeholder:text-white/30"
                  />
                </div>

                {/* Outcome Selection in Edit Mode */}
                <div>
                  <label className="text-xs text-white/60 mb-1 block font-mono">Trade Outcome</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setSelectedOutcome("win")}
                      data-testid="edit-outcome-win"
                      className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                        selectedOutcome === "win"
                          ? "bg-[#00E676]/20 border-[#00E676] text-[#00E676]"
                          : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                      }`}
                    >
                      WIN
                    </button>
                    <button
                      onClick={() => setSelectedOutcome("loss")}
                      data-testid="edit-outcome-loss"
                      className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                        selectedOutcome === "loss"
                          ? "bg-[#FF3B30]/20 border-[#FF3B30] text-[#FF3B30]"
                          : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                      }`}
                    >
                      LOSS
                    </button>
                    <button
                      onClick={() => setSelectedOutcome("pending")}
                      data-testid="edit-outcome-pending"
                      className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                        selectedOutcome === "pending"
                          ? "bg-white/10 border-white text-white"
                          : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                      }`}
                    >
                      PENDING
                    </button>
                  </div>
                </div>

                {/* Position Status Selection - Only visible for open positions (pending outcome) */}
                {selectedOutcome === "pending" && (
                  <div>
                    <label className="text-xs text-white/60 mb-1 block font-mono">Position Status</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setPositionStatus("active")}
                        data-testid="position-status-active"
                        className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                          positionStatus === "active"
                            ? "bg-[#00E676]/20 border-[#00E676] text-[#00E676]"
                            : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                        }`}
                      >
                        ACTIVE
                      </button>
                      <button
                        onClick={() => setPositionStatus("pending")}
                        data-testid="position-status-pending"
                        className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                          positionStatus === "pending"
                            ? "bg-[#F59E0B]/20 border-[#F59E0B] text-[#F59E0B]"
                            : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                        }`}
                      >
                        PENDING
                      </button>
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                      {positionStatus === "active" ? "Trade is already entered" : "Waiting for price to reach entry zone"}
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-trade-button"
                    className="flex-1 bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90 transition-colors rounded-sm"
                  >
                    {saving ? (
                      <>
                        <ArrowsClockwise size={20} weight="bold" className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check size={20} weight="bold" className="mr-2" />
                        Save Trade
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => setEditedData(analysisResult)}
                    data-testid="discard-changes-button"
                    variant="outline"
                    className="border-white/10 text-white/60 hover:text-white hover:bg-white/5 rounded-sm"
                  >
                    <X size={20} weight="bold" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-testid="view-mode-section">
                <div className="bg-[#000000] border border-white/10 rounded-sm p-4 font-mono text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-white/40 text-xs mb-1">TRADE STATUS</div>
                      <div className="text-white uppercase" data-testid="result-trade-status">{analysisResult.trade_status}</div>
                    </div>
                    <div>
                      <div className="text-white/40 text-xs mb-1">DIRECTION</div>
                      <div className={`font-bold uppercase ${analysisResult.direction === 'long' ? 'text-[#00E676]' : 'text-[#FF3B30]'}`} data-testid="result-direction">
                        {analysisResult.direction}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#000000] border border-white/10 rounded-sm p-4 font-mono text-sm">
                  <div className="text-white/40 text-xs mb-2">ASSET</div>
                  <div className="text-white text-lg font-bold" data-testid="result-asset">{analysisResult.asset}</div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className={`bg-[#000000] rounded-sm p-4 ${tradeValidationError ? 'border border-[#FF3B30]/50' : 'border border-white/10'}`}>
                    <div className="text-white/40 text-xs mb-1 font-mono">ENTRY</div>
                    <div className="text-white font-mono text-lg" data-testid="result-entry">{analysisResult.entry}</div>
                  </div>
                  <div className={`bg-[#000000] rounded-sm p-4 ${tradeValidationError?.field === 'stop_loss' ? 'border border-[#FF3B30]/50' : 'border border-white/10'}`}>
                    <div className="text-white/40 text-xs mb-1 font-mono">STOP LOSS</div>
                    <div className="text-white font-mono text-lg" data-testid="result-stop-loss">{analysisResult.stop_loss}</div>
                  </div>
                  <div className={`bg-[#000000] rounded-sm p-4 ${tradeValidationError?.field === 'take_profit' ? 'border border-[#FF3B30]/50' : 'border border-white/10'}`}>
                    <div className="text-white/40 text-xs mb-1 font-mono">TAKE PROFIT</div>
                    <div className="text-white font-mono text-lg" data-testid="result-take-profit">{analysisResult.take_profit}</div>
                  </div>
                </div>

                {/* Trade Validation Error - Analysis View */}
                {tradeValidationError && (
                  <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm px-3 py-2 flex items-center gap-2" data-testid="trade-validation-error-analysis">
                    <X size={16} className="text-[#FF3B30] flex-shrink-0" />
                    <span className="text-[#FF3B30] text-sm">{tradeValidationError.message}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs mb-1 font-mono">RR RATIO (AUTO)</div>
                    <div className="text-white font-mono text-2xl font-bold" data-testid="result-rr">
                      {editedData?.rr != null && !isNaN(editedData.rr) 
                        ? `${editedData.rr.toFixed(2)}R` 
                        : <span className="text-white/40">-</span>
                      }
                    </div>
                  </div>
                  <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                    <div className="text-white/40 text-xs mb-1 font-mono">CONFIDENCE</div>
                    <select
                      value={
                        editedData?.confidence || '__not_tracked__'
                      }
                      onChange={async (e) => {
                        const value = e.target.value;
                        // Clear error when user interacts
                        setConfidenceError(null);
                        
                        if (value === '__not_tracked__') {
                          // Show warning modal only on first manual selection (not on default)
                          if (!hasConfirmedDoNotTrackConfidence && editedData?.confidence !== null) {
                            setPendingConfidenceDisable(true);
                            setShowConfidenceDisableModal(true);
                          } else {
                            // Already confirmed or re-selecting after initial default
                            handleFieldChange("confidence", null);
                          }
                        } else if (value !== '') {
                          handleFieldChange("confidence", value);
                        }
                      }}
                      data-testid="result-confidence"
                      className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2 text-sm font-mono focus:outline-none uppercase ${
                        confidenceError ? 'border-[#FF3B30]' : 'border-white/10 focus:border-white'
                      } ${
                        editedData?.confidence === 'high' ? 'text-[#00E676]' :
                        editedData?.confidence === 'medium' ? 'text-[#FFA726]' :
                        editedData?.confidence === 'low' ? 'text-[#FF3B30]' :
                        'text-white/50'
                      }`}
                    >
                      <option value="high" className="text-[#00E676]">High</option>
                      <option value="medium" className="text-[#FFA726]">Medium</option>
                      <option value="low" className="text-[#FF3B30]">Low</option>
                      <option value="__not_tracked__" className="text-white/50">Do not track this</option>
                    </select>
                    {confidenceError && (
                      <div className="text-[#FF3B30] text-[10px] mt-1 font-mono" data-testid="confidence-error">
                        {confidenceError}
                      </div>
                    )}
                  </div>
                </div>

                {/* Manual Notes Input */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                  <div className="text-white/40 text-xs mb-2 font-mono">YOUR NOTES (OPTIONAL)</div>
                  <textarea
                    value={editedData.notes}
                    onChange={(e) => handleFieldChange("notes", e.target.value)}
                    data-testid="result-notes-input"
                    placeholder="Add your trade notes here..."
                    rows={3}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white/80 text-sm rounded-sm px-3 py-2 focus:border-white focus:outline-none resize-none placeholder:text-white/30"
                  />
                </div>

                {/* Setup and Outcome Selection */}
                <div className="space-y-4 pt-2">
                  <div>
                    <label className="text-xs text-white/60 mb-2 block font-mono">SELECT SETUP *</label>
                    <select
                      value={selectedSetup}
                      onChange={handleSetupChange}
                      data-testid="select-setup"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none"
                    >
                      <option value="">Select a setup</option>
                      {setups.map((setup) => (
                        <option key={setup.id} value={setup.id}>
                          {setup.name}
                        </option>
                      ))}
                      <option value="__create_new__" className="text-[#00E676]">+ Create setup</option>
                    </select>
                    {setupsError && (
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-[#F59E0B]" data-testid="result-setups-error">
                        <Warning size={12} weight="fill" />
                        <span>Failed to load your setups.</span>
                        <button
                          type="button"
                          onClick={fetchSetups}
                          data-testid="retry-result-setups-btn"
                          className="underline hover:text-[#F59E0B]/80"
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-white/60 mb-2 block font-mono">ASSET GROUP (OPTIONAL)</label>
                    <select
                      value={selectedAssetGroup}
                      onChange={(e) => {
                        setSelectedAssetGroup(e.target.value);
                        if (assetGroupDetected) {
                          setAssetGroupDetected(false);
                        }
                      }}
                      data-testid="select-asset-group"
                      className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none ${
                        assetGroupDetected 
                          ? 'border-[#00E676]/30 text-white' 
                          : 'border-white/10 text-white'
                      }`}
                    >
                      <option value="">No group</option>
                      {assetGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                    {assetGroupDetected && (
                      <p className="text-xs text-[#00E676]/80 mt-2">
                        ✓ Auto-matched based on asset
                      </p>
                    )}
                  </div>

                  {/* Expectancy Evaluation Display */}
                  {expectancyEvaluation && (
                    <div 
                      className={`rounded-sm p-3 border ${
                        expectancyEvaluation.status === 'unfavorable' 
                          ? 'bg-[#FF3B30]/10 border-[#FF3B30]/30' 
                          : expectancyEvaluation.status === 'borderline'
                          ? 'bg-[#FF9800]/10 border-[#FF9800]/30'
                          : expectancyEvaluation.status === 'favorable'
                          ? 'bg-[#00E676]/10 border-[#00E676]/30'
                          : 'bg-white/5 border-white/10'
                      }`}
                      data-testid="expectancy-evaluation"
                    >
                      {expectancyEvaluation.status === 'insufficient_data' ? (
                        <div className="flex items-center gap-2">
                          <Hourglass size={16} className="text-white/40" />
                          <span className="text-white/60 text-xs">{expectancyEvaluation.message}</span>
                          <span className="text-white/40 text-xs">({expectancyEvaluation.totalTrades} trades)</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {expectancyEvaluation.status === 'unfavorable' && (
                                <Warning size={16} weight="fill" className="text-[#FF3B30]" />
                              )}
                              {expectancyEvaluation.status === 'borderline' && (
                                <Warning size={16} weight="fill" className="text-[#FF9800]" />
                              )}
                              {expectancyEvaluation.status === 'favorable' && (
                                <Check size={16} weight="bold" className="text-[#00E676]" />
                              )}
                              <span className={`text-xs font-bold uppercase ${
                                expectancyEvaluation.status === 'unfavorable' ? 'text-[#FF3B30]' :
                                expectancyEvaluation.status === 'borderline' ? 'text-[#FF9800]' :
                                'text-[#00E676]'
                              }`}>
                                {expectancyEvaluation.status}
                              </span>
                            </div>
                            <span className="text-white/40 text-[10px]">{expectancyEvaluation.totalTrades} trades</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <span className="text-white/40 text-[10px] font-mono">SETUP WIN RATE</span>
                              <div className="text-white font-mono text-sm font-bold">
                                {expectancyEvaluation.setupWinRate.toFixed(1)}%
                              </div>
                            </div>
                            <div>
                              <span className="text-white/40 text-[10px] font-mono">REQUIRED WIN RATE</span>
                              <div className="text-white font-mono text-sm font-bold">
                                {expectancyEvaluation.requiredWinRate.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                          <p className="text-white/60 text-xs">{expectancyEvaluation.message}</p>
                        </>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-white/60 mb-2 block font-mono">SELECT TIMEFRAME *</label>
                    <select
                      value={selectedTimeframe}
                      onChange={(e) => {
                        setSelectedTimeframe(e.target.value);
                        if (timeframeDetected) {
                          setTimeframeDetected(false);
                        }
                      }}
                      data-testid="select-timeframe"
                      className={`w-full bg-[#0A0A0A] border rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none ${
                        timeframeDetected 
                          ? 'border-[#00E676]/30 text-white' 
                          : 'border-white/10 text-white'
                      }`}
                    >
                      <option value="">Choose timeframe...</option>
                      {timeframes.map((tf) => (
                        <option key={tf} value={tf}>
                          {tf}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-white/40 mt-2">
                      &lt;4H = Day Trade | ≥4H = Swing Trade
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-white/60 mb-2 block font-mono">HAND SIZE (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={handSize}
                      onChange={(e) => {
                        setHandSize(e.target.value);
                        setHandSizeEdited(true);
                      }}
                      data-testid="hand-size-input"
                      placeholder="Enter position size in USD"
                      className="w-full bg-[#0A0A0A] border border-white/10 text-white rounded-sm px-3 py-2 text-sm font-mono focus:border-white focus:outline-none placeholder:text-white/30"
                    />
                    <p className="text-xs text-white/40 mt-2">
                      Amount risked on this trade
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-white/60 mb-2 block font-mono">TRADE OUTCOME</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => setSelectedOutcome("win")}
                        data-testid="outcome-win"
                        className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                          selectedOutcome === "win"
                            ? "bg-[#00E676]/20 border-[#00E676] text-[#00E676]"
                            : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                        }`}
                      >
                        WIN
                      </button>
                      <button
                        onClick={() => setSelectedOutcome("loss")}
                        data-testid="outcome-loss"
                        className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                          selectedOutcome === "loss"
                            ? "bg-[#FF3B30]/20 border-[#FF3B30] text-[#FF3B30]"
                            : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                        }`}
                      >
                        LOSS
                      </button>
                      <button
                        onClick={() => setSelectedOutcome("pending")}
                        data-testid="outcome-pending"
                        className={`px-3 py-2 text-sm font-mono rounded-sm border transition-colors ${
                          selectedOutcome === "pending"
                            ? "bg-white/10 border-white text-white"
                            : "bg-[#0A0A0A] border-white/10 text-white/60 hover:border-white/20"
                        }`}
                      >
                        PENDING
                      </button>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-analysis-button"
                  className="w-full bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm py-3"
                >
                  {saving ? (
                    <>
                      <ArrowsClockwise size={20} weight="bold" className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={20} weight="bold" className="mr-2" />
                      Save to History
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Progress / Momentum Section - Always visible, compact */}
      {/* HIDDEN in shared/public profile view - private user metrics */}
      {/* Also hidden when progress stage is 'early' to remove 10-trade limit and progress bar visual clutter */}
      {!isSharedProfileView && !tradesLoading && progressState && progressState.stage !== 'early' && (
        <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pt-6" data-testid="progress-section">
          <div className="bg-gradient-to-b from-[#121212] to-[#0D0D0D] border border-white/10 rounded-sm overflow-hidden">
            {/* Compact Content */}
            <div className="px-5 py-4">
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className={`w-9 h-9 rounded-sm flex items-center justify-center border shrink-0 ${
                  progressState.stage === 'consistent' 
                    ? 'bg-gradient-to-br from-[#00E676]/20 to-[#00E676]/5 border-[#00E676]/20'
                    : progressState.stage === 'growing'
                    ? 'bg-gradient-to-br from-[#00B8D4]/20 to-[#00B8D4]/5 border-[#00B8D4]/20'
                    : 'bg-gradient-to-br from-white/10 to-white/5 border-white/10'
                }`}>
                  <ChartLine size={18} weight="duotone" className={
                    progressState.stage === 'consistent' 
                      ? 'text-[#00E676]'
                      : progressState.stage === 'growing'
                      ? 'text-[#00B8D4]'
                      : 'text-white/50'
                  } />
                </div>
                
                {/* Main Content */}
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-semibold ${
                    progressState.stage === 'consistent' 
                      ? 'text-[#00E676]'
                      : progressState.stage === 'growing'
                      ? 'text-[#00B8D4]'
                      : 'text-white/80'
                  }`}>
                    {progressState.title}
                  </h3>
                  <p className="text-xs text-white/40 mt-0.5">{progressState.messages[0]}</p>
                </div>

                {/* Progress Bar (only for early stage) */}
                {progressState.stage === 'early' && (
                  <div className="shrink-0 flex items-center gap-3">
                    <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-white/40 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((progressState.tradeCount / 10) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-mono text-white/50">
                      {progressState.tradeCount}/10
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insights Section - Collapsible */}
      {/* HIDDEN in shared/public profile view - private user metrics */}
      {!isSharedProfileView && (
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 pt-4 pb-8" data-testid="insights-section">
        <div className="bg-gradient-to-b from-[#121212] to-[#0D0D0D] border border-white/10 rounded-sm overflow-hidden">
          {/* Collapsible Header */}
          <button
            onClick={() => setInsightsExpanded(!insightsExpanded)}
            className="w-full px-5 py-3 flex items-center gap-3 hover:bg-white/[0.02] transition-colors cursor-pointer"
            data-testid="insights-header-toggle"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-[#00E676]/15 to-[#00E676]/5 rounded-sm flex items-center justify-center border border-[#00E676]/15">
              <Lightbulb size={18} weight="duotone" className="text-[#00E676]" />
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold font-heading text-white tracking-tight">Insights</h2>
                {/* Insight count badge - shows TOTAL count */}
                {!tradesLoading && allTrades.length >= INSIGHTS_MIN_TRADES && allInsights.length > 0 && (
                  <span className="text-xs font-mono text-[#00E676]/70 bg-[#00E676]/10 px-1.5 py-0.5 rounded">
                    {allInsights.length}
                  </span>
                )}
                {/* New badge for unseen insights */}
                {hasUnseenInsights && !insightsExpanded && (
                  <span className="text-[10px] font-bold text-white bg-[#00E676] px-1.5 py-0.5 rounded uppercase tracking-wide">
                    New
                  </span>
                )}
              </div>
            </div>
            {/* Chevron */}
            <div className={`text-white/40 transition-transform duration-200 ${insightsExpanded ? 'rotate-180' : ''}`}>
              <CaretDown size={16} weight="bold" />
            </div>
          </button>

          {/* Collapsible Content */}
          <div 
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              insightsExpanded ? (insightsShowAll ? 'max-h-[1200px]' : 'max-h-[600px]') + ' opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-5 py-4 border-t border-white/5">
              {/* Loading State */}
              {tradesLoading && (
                <div className="flex items-center justify-center gap-3 py-4 text-white/40">
                  <ArrowsClockwise size={16} className="animate-spin" />
                  <span className="text-sm">{t('dashboard.analyzingTrades')}</span>
                </div>
              )}

              {/* Not Enough Data State - Locked */}
              {!tradesLoading && allTrades.length < INSIGHTS_MIN_TRADES && (
                <div className="flex items-center gap-4 py-2">
                  <div className="w-10 h-10 bg-white/5 rounded-sm flex items-center justify-center shrink-0 border border-white/10">
                    <Lock size={20} weight="duotone" className="text-white/30" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/70 text-sm font-medium">
                      {language === 'ptBR' 
                        ? `Insights desbloqueiam com ${INSIGHTS_MIN_TRADES} trades` 
                        : `Insights unlock at ${INSIGHTS_MIN_TRADES} trades`}
                    </p>
                    <p className="text-white/40 text-xs mt-0.5">
                      {language === 'ptBR' 
                        ? 'Registre mais trades para começar a ver padrões' 
                        : 'Log more trades to start seeing patterns'}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <span className="text-xs font-mono text-white/50">
                      {allTrades.length}/{INSIGHTS_MIN_TRADES}
                    </span>
                  </div>
                </div>
              )}

              {/* Insights List */}
              {!tradesLoading && allTrades.length >= INSIGHTS_MIN_TRADES && (
                <div className="space-y-2">
                  {insights.length === 0 ? (
                    /* Progress State - No strong insights but have data */
                    <div className="flex items-start gap-3 p-3 rounded-sm border bg-[#00E676]/5 border-[#00E676]/10">
                      <div className="w-7 h-7 rounded flex items-center justify-center shrink-0 bg-[#00E676]/15">
                        <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                      </div>
                      <div className="flex-1 py-0.5">
                        <p className="text-sm text-white/90 leading-snug">
                          {language === 'ptBR' ? 'Você está começando a construir padrões' : "You're starting to build patterns"}
                        </p>
                        <p className="text-xs text-white/40 mt-1">
                          {language === 'ptBR' 
                            ? `${allTrades.filter(t => t.status === 'closed').length} trades fechados — mais padrões surgirão conforme seus dados crescem`
                            : `${allTrades.filter(t => t.status === 'closed').length} closed trades logged — more patterns will emerge as your dataset grows`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Show TOP 3 insights or ALL if expanded
                    (insightsShowAll ? allInsights : insights).map((insight, idx) => {
                      const isClickable = insight.link && insight.link.path;
                      
                      const handleInsightClick = () => {
                        if (!isClickable) return;
                        const { path, params } = insight.link;
                        if (params) {
                          const queryString = new URLSearchParams(params).toString();
                          navigate(`${path}?${queryString}`);
                        } else {
                          navigate(path);
                        }
                      };
                      
                      return (
                        <div 
                          key={idx}
                          onClick={handleInsightClick}
                          className={`group flex items-start gap-3 p-3 rounded-sm border transition-all duration-200 ${
                            insight.type === 'positive' 
                              ? 'bg-[#00E676]/5 border-[#00E676]/10' 
                              : insight.type === 'warning'
                              ? 'bg-[#FFC107]/5 border-[#FFC107]/10'
                              : insight.type === 'negative'
                              ? 'bg-[#FF3B30]/5 border-[#FF3B30]/10'
                              : 'bg-white/[0.02] border-white/10'
                          } ${isClickable ? 'cursor-pointer hover:border-white/30 hover:bg-white/[0.03]' : ''}`}
                          data-testid={`insight-${idx}`}
                        >
                          <div className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${
                            insight.type === 'positive' 
                              ? 'bg-[#00E676]/15' 
                              : insight.type === 'warning'
                              ? 'bg-[#FFC107]/15'
                              : insight.type === 'negative'
                              ? 'bg-[#FF3B30]/15'
                              : 'bg-white/10'
                          }`}>
                            {insight.type === 'positive' ? (
                              <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                            ) : insight.type === 'warning' ? (
                              <Warning size={16} weight="bold" className="text-[#FFC107]" />
                            ) : insight.type === 'negative' ? (
                              <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                            ) : (
                              <Lightbulb size={16} weight="bold" className="text-white/60" />
                            )}
                          </div>
                          <div className="flex-1 py-0.5">
                            <p className="text-sm text-white/90 leading-snug">
                              {insight.text}
                            </p>
                            {insight.subtext && (
                              <p className="text-xs text-white/50 mt-1 leading-snug">
                                {insight.subtext}
                              </p>
                            )}
                            {insight.bullets && insight.bullets.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {insight.bullets.map((bullet, bIdx) => (
                                  <li key={bIdx} className="text-xs text-white/40 flex items-start gap-2">
                                    <span className="text-white/30 mt-0.5">•</span>
                                    <span>{bullet}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {/* Arrow indicator for clickable insights */}
                          {isClickable && (
                            <div className="shrink-0 self-center opacity-40 group-hover:opacity-80 transition-opacity">
                              <ArrowRight size={16} weight="bold" className="text-white" />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  
                  {/* View All / Show Less - inline expansion */}
                  {allInsights.length > 3 && (
                    <button
                      onClick={() => setInsightsShowAll(!insightsShowAll)}
                      className="w-full mt-3 py-2 text-center text-xs text-white/40 hover:text-white/70 transition-colors border-t border-white/5"
                      data-testid="view-all-insights-btn"
                    >
                      {insightsShowAll 
                        ? 'Show Less' 
                        : `View All Insights (${allInsights.length})`
                      }
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Pending Payment Banner - Shows when user has pending payment with bonus trades */}
      {showPendingBanner && (
        <div 
          className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-[#F0B90B]/10 border border-[#F0B90B]/30 rounded-sm px-6 py-3 flex items-center gap-4 z-40"
          data-testid="pending-payment-banner"
        >
          <Hourglass size={24} weight="fill" className="text-[#F0B90B]" />
          <div>
            {bonusRemaining > 0 ? (
              <>
                <p className="text-white font-semibold text-sm">
                  Payment pending · {bonusRemaining} bonus trade{bonusRemaining !== 1 ? 's' : ''} left
                </p>
                <p className="text-white/50 text-xs">
                  Your upgrade is under review
                </p>
              </>
            ) : (
              <>
                <p className="text-white font-semibold text-sm">
                  Payment pending · waiting for approval
                </p>
                <p className="text-white/50 text-xs">
                  Your upgrade is being processed
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Trade Limit Warning Banner - Only shown to FREE users at limit without pending payment */}
      {showLimitBanner && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 border border-[#FFD700]/30 rounded-sm px-6 py-3 flex items-center gap-4 z-40">
          <Crown size={24} weight="fill" className="text-[#FFD700]" />
          <div>
            <p className="text-white font-semibold text-sm">
              You've reached your free trade limit ({FREE_TRADE_LIMIT} trades)
            </p>
            <p className="text-white/60 text-xs">
              Upgrade to Pro for unlimited trades
            </p>
          </div>
          <Button
            onClick={() => setShowUpgradeModal(true)}
            className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold hover:opacity-90 px-4"
            data-testid="upgrade-banner-btn"
          >
            Upgrade Now
          </Button>
        </div>
      )}

      {/* Upgrade Modal - Only show if no pending payment */}
      {/* Upgrade Modal - Plan Selection */}
      <UpgradeModal
        isOpen={showUpgradeModal && !hasPendingPayment}
        onClose={() => setShowUpgradeModal(false)}
        onSelectPlan={handleUpgradeClick}
        hasPendingPayment={hasPendingPayment}
        userEmail={userEmail}
      />

      {/* Crypto Payment Modal */}
      <CryptoPaymentModal
        isOpen={showCryptoModal}
        onClose={() => setShowCryptoModal(false)}
        onPaymentSubmitted={handleCryptoPaymentSubmitted}
        existingPendingPayment={pendingCryptoPayment}
        initialPlan={cryptoModalPlan}
        onBack={() => {
          setShowCryptoModal(false);
          setShowUpgradeModal(true);
        }}
      />

      {/* Pro Success Modal - Shows when user just became Pro */}
      {showProSuccessModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={async (e) => {
            if (e.target === e.currentTarget) {
              // Mark Pro welcome as seen in backend
              try {
                await api.post('/user/pro-welcome-seen');
              } catch (error) {
                console.error('Error marking pro welcome seen:', error);
              }
              setShowProSuccessModal(false);
            }
          }}
          data-testid="pro-success-modal-overlay"
        >
          <div 
            className="bg-[#121212] border border-[#F0B90B]/40 rounded-sm p-8 w-full max-w-md mx-4 relative"
            data-testid="pro-success-modal"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-[#F0B90B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl text-[#F0B90B]">✦</span>
              </div>
              <h2 className="text-3xl font-black font-heading tracking-tight mb-2">You're now Pro <span className="text-[#F0B90B]">✦</span></h2>
              <p className="text-white/70 text-lg">
                Congratulations — you're now one step closer to achieving your trading goals.
              </p>
            </div>

            {/* Secondary message */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-4 mb-6">
              <p className="text-white/50 text-sm text-center">
                If you encounter any issues or have feature requests, go to <span className="text-white/70">User → Report</span>.
              </p>
            </div>

            {/* Button */}
            <Button
              onClick={async () => {
                // Mark Pro welcome as seen in backend
                try {
                  await api.post('/user/pro-welcome-seen');
                } catch (error) {
                  console.error('Error marking pro welcome seen:', error);
                }
                setShowProSuccessModal(false);
                // Clear the pending payment flag
                setHasPendingPayment(false);
                // Re-fetch to update UI
                fetchSubscriptionStatus();
                checkPendingPayment();
              }}
              className="w-full bg-[#F0B90B] text-black font-bold hover:bg-[#D4A50A]"
              data-testid="pro-success-modal-close"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Confidence Disable Confirmation Modal */}
      {showConfidenceDisableModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConfidenceDisableModal(false);
              setPendingConfidenceDisable(false);
            }
          }}
          data-testid="confidence-disable-modal-overlay"
        >
          <div 
            className="bg-[#121212] border border-[#FFA726]/40 rounded-sm p-6 w-full max-w-md mx-4"
            data-testid="confidence-disable-modal"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#FFA726]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">⚠️</span>
              </div>
              <h2 className="text-xl font-bold font-heading mb-2">Do not track confidence?</h2>
              <p className="text-white/60 text-sm">
                Important insights may stop appearing if you do not track confidence. Are you sure?
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowConfidenceDisableModal(false);
                  setPendingConfidenceDisable(false);
                }}
                variant="outline"
                className="flex-1 border-white/20 text-white/80 hover:bg-white/5"
                data-testid="confidence-keep-tracking"
              >
                Keep tracking
              </Button>
              <Button
                onClick={async () => {
                  // Mark as confirmed so modal won't show again
                  setHasConfirmedDoNotTrackConfidence(true);
                  try {
                    localStorage.setItem('hasConfirmedDoNotTrackConfidence', 'true');
                  } catch (e) {
                    console.error('Error saving hasConfirmedDoNotTrackConfidence to localStorage:', e);
                  }
                  // Persist to backend for cross-device sync
                  try {
                    await api.put('/user/profile', {
                      has_confirmed_do_not_track_confidence: true
                    });
                  } catch (error) {
                    console.error('Error saving hasConfirmedDoNotTrackConfidence to backend:', error);
                  }
                  // Update the current trade's confidence to null
                  handleFieldChange("confidence", null);
                  setShowConfidenceDisableModal(false);
                  setPendingConfidenceDisable(false);
                }}
                className="flex-1 bg-[#FFA726] text-black font-bold hover:bg-[#FB8C00]"
                data-testid="confidence-disable-confirm"
              >
                Yes, do not track
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Setup Dialog - Reused from Analytics pattern */}
      <Dialog open={showCreateSetupModal} onOpenChange={setShowCreateSetupModal}>
        <DialogContent className="bg-[#121212] border border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">Create New Setup</DialogTitle>
            <DialogDescription className="text-white/60">
              Add a new trading setup to track its performance
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-setup-name" className="text-white/80 mb-2 block">Setup Name</Label>
              <Input
                id="new-setup-name"
                data-testid="new-setup-name-input"
                placeholder="e.g., Breakout, Support/Resistance"
                value={newSetupName}
                onChange={(e) => setNewSetupName(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="new-setup-description" className="text-white/80 mb-2 block">Description (Optional)</Label>
              <Input
                id="new-setup-description"
                data-testid="new-setup-description-input"
                placeholder="Brief description of this setup"
                value={newSetupDescription}
                onChange={(e) => setNewSetupDescription(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateSetupModal(false);
                setNewSetupName("");
                setNewSetupDescription("");
              }}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSetup}
              disabled={creatingSetup || !newSetupName.trim()}
              data-testid="confirm-create-setup-btn"
              className="bg-white text-black font-bold hover:bg-gray-200"
            >
              {creatingSetup ? (
                <>
                  <ArrowsClockwise size={16} className="animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} className="mr-2" />
                  Create Setup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Low Expectancy Warning Modal */}
      <Dialog open={showExpectancyWarning} onOpenChange={(open) => {
        if (!open) handleCancelExpectancyWarning();
      }}>
        <DialogContent className="bg-[#121212] border border-[#FF3B30]/30">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Warning size={24} weight="fill" className="text-[#FF3B30]" />
              Low Expectancy Trade
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Based on your historical data, this trade structure has a negative expectancy.
            </DialogDescription>
          </DialogHeader>
          
          {expectancyEvaluation && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                  <div className="text-white/40 text-xs font-mono mb-1">SETUP WIN RATE</div>
                  <div className="text-2xl font-bold font-mono text-white">
                    {expectancyEvaluation.setupWinRate?.toFixed(1)}%
                  </div>
                </div>
                <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                  <div className="text-white/40 text-xs font-mono mb-1">REQUIRED WIN RATE</div>
                  <div className="text-2xl font-bold font-mono text-[#FF3B30]">
                    {expectancyEvaluation.requiredWinRate?.toFixed(1)}%
                  </div>
                </div>
              </div>
              
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-sm p-3">
                <p className="text-white/80 text-sm">
                  This trade structure is statistically unfavorable for this setup. Your win rate is below the required breakeven threshold for this RR ratio.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCancelExpectancyWarning}
              className="bg-transparent border-white/10 text-white hover:bg-white/5"
              data-testid="cancel-expectancy-warning"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmedSave}
              className="bg-[#FF3B30] text-white font-bold hover:bg-[#FF3B30]/80"
              data-testid="confirm-expectancy-warning"
            >
              I Understand, Save Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trading Alerts Popup */}
      <AlertPopup alerts={alerts} onDismiss={dismissAlert} />
    </div>
  );
}